/* Chessrebels Puzzle Widget (single-file asset)
   Mount element: #mate2-widget-mount

   ✅ FIXES INCLUDED (2026-03-05)
   1) Data-source compatible: applies the CSV "pre-move" (first UCI in Moves) like the original logic.
      => fixes "WHITE TO MOVE" on black puzzles and keeps Lichess-style puzzle flow.
   2) chess.js is LAZY-INITIALIZED:
      - if chess.js wasn't loaded yet when a puzzle was shown, we build chessGame later (on first interaction)
      - replays: FEN -> pre-move -> already played plies (step)
      => fixes "can't move pieces" / moves rejected when chess.js loads late.
   3) ✅ FIXED (critical): Accept ANY LEGAL player move that results in CHECKMATE (including NON-promotion mates).
      => fixes “only one mate move accepted” when mate move has no promotion.
   4) ✅ Click-to-move selection allows selecting ANY own piece.
   5) chess.js remains the single source of truth when available:
      - after every move, UI position `pos` is rebuilt from chessGame.fen()
      => prevents desync that can break alternative mates.

   Note: If chess.js fails to load, widget still works in strict-line mode (no alternative mates).
*/
(() => {
  const MOUNT_ID = "mate2-widget-mount";

  /* ========= SHARING CONFIG ========= */
  const SHARE_SITE_URL = "https://chessrebels.com";

  /* ========= GLOBAL LEADERBOARD CONFIG (Google Apps Script Web App) ========= */
  const LEADERBOARD_API_URL =
    "https://script.google.com/macros/s/AKfycbzZL_noCITnyN-zaI4XSu_ppI8ZFl7AROdBoXGY16Aq3mC83K-g_T2pmJmk7r1y9dyjEw/exec";
  const LEADERBOARD_TOKEN = "cr_4x9q7h5k3b9";
  const GLOBAL_TOP_LIMIT = 5;

  /* ========= CHESS ENGINE (for mate alternatives) ========= */
  const CHESSJS_URL =
    "https://cdn.jsdelivr.net/npm/chess.js@1.0.0-beta.8/dist/chess.min.js";

  /* ========= PUZZLE DATA ========= */
const DATA_URL = "https://chessrebels.com/cdn/shop/t/1/assets/mate_in_3_1000.csv";
const LS_KEY = "mate3_puzzles_v5";

  const FALLBACKS = [
    {
      id: "fallback-mate3",
      fen: "6k1/5ppp/8/8/8/4Q3/5PPP/6K1 w - - 0 1",
      moves: "e3e8 g8h7 e8e4 h7g8 e4e8",
    },
  ];

  /* ========= REWARDS ========= */
  const BADGE_REWARDS = {
  bronze: { code: "Q8MQ45H4PSTC", label: "10% OFF", redirect: "/collections/men" },
  silver: { code: "0ZB9MPJ471VN", label: "12% OFF", redirect: "/collections/men" },
  gold:   { code: "VZJBHKA9B0R9", label: "15% OFF", redirect: "/collections/men" }
};


  /* ========= RUN CONFIG ========= */
  const RUN_SECONDS = 180;
  const REWARD_MILESTONES = [3, 6, 9];
  const PANIC_LAST_SECONDS = 5;

  /* ======= TIMING TUNES ======= */
  const PIECE_GLIDE_MS = 210;
  const PIECE_GLIDE_MS_MOBILE = 180;
  const EXPLODE_SCALE = 1.5;
  const SOLVE_POP_MS = 2600;
  const SOLVE_POP_CLEAR_MS = 2740;
  const POST_SOLVE_TOAST_MS = 2000;
  const COMBO_X2_MS = 8000;
  const COMBO_X3_MS = 6000;

  /* ========= SFX URLs ========= */
  const SFX_URLS = {
    countdown: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/CountdownSound.mp3?v=1772382738",
    capture: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/CaptureSound.mp3?v=1772382787",
    check: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/newchecksound.mp3?v=1773488283",
  };
  const SFX_VOL = { countdown: 0.85, capture: 0.9, check: 0.85 };
  const CHECK_SFX_COOLDOWN_MS = 260;

  /* ======= PIECE IMAGES ======= */
  const PIECE_URLS = {
    wK: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wK.svg?v=1756306532",
    wQ: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wQ.svg?v=1756306582",
    wR: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wR.svg?v=1756306582",
    wB: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wB.svg?v=1756306582",
    wN: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wN.svg?v=1756306582",
    wP: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/wP.svg?v=1756306582",
    bK: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bK.svg?v=1756306582",
    bQ: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bQ.svg?v=1756306582",
    bR: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bR.svg?v=1756306582",
    bB: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bB.svg?v=1756306582",
    bN: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bN.svg?v=1756306582",
    bP: "https://cdn.shopify.com/s/files/1/0895/6103/9184/files/bP.svg?v=1756306583",
  };

  /* ========= HTML TEMPLATE ========= */
  const HTML = `
  <div id="mate2-widget"
    style="--bg:#fff;--fg:#111827;--muted:#6b7280;--ring:#60a5fa;--bd:#e5e7eb;
    --sq-light:#00f5df;--sq-dark:#F40063;--wm-size:60%;--wm-opacity:.12;--board-h:auto;

    --hud-bg: rgba(255,255,255,.14);
    --hud-bd: rgba(255,255,255,.22);
    --hud-shadow: 0 10px 26px rgba(0,0,0,.14);
    --hud-text-opacity: .82;
    --hud-dot-opacity: .70;

    max-width:1280px;margin:12px auto;padding:14px;border:1px solid var(--bd);border-radius:16px;
    box-shadow:0 8px 24px rgba(0,0,0,.08);background:var(--bg);color:var(--fg);position:relative;overflow:visible;
    z-index:0;
    isolation:isolate;
    ">




    <style>

     @import url("https://fonts.googleapis.com/css2?family=Archivo Black&display=swap");
      #mate2-widget{ position:relative; z-index:0; isolation:isolate; }
      @media (prefers-color-scheme: dark){
        #mate2-widget{
          --bg:#0b0f14; --fg:#e5e7eb; --muted:#9ca3af; --bd:#1f2937;
          --hud-bg: rgba(0,0,0,.22);
          --hud-bd: rgba(255,255,255,.18);
          --hud-shadow: 0 12px 30px rgba(0,0,0,.22);
          --hud-text-opacity: .80;
          --hud-dot-opacity: .68;
        }
      }
      #mate2-widget{ --cr-font:"Archivo black", system-ui, sans-serif; }
      #mate2-widget, #mate2-widget *{
        font-family: var(--cr-font);
        -webkit-font-smoothing: auto;
        -moz-osx-font-smoothing: auto;
        text-rendering: geometricPrecision;
      }
      #mate2-widget .arcade{
        color:#fff;
        text-transform:uppercase;
        letter-spacing:.7px;
        -webkit-text-stroke: 0 transparent;
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 4px 0 rgba(0,0,0,.35);
      }
      #mate2-widget .arcade.small{
        -webkit-text-stroke: 0 transparent;
        text-shadow:
          1px 0 #000, -1px 0 #000, 0 1px #000, 0 -1px #000,
          1px 1px #000, -1px 1px #000, 1px -1px #000, -1px -1px #000,
          0 3px 0 rgba(0,0,0,.30);
      }
      #mate2-widget mini-chessboard{
        border:3px solid #000;
        border-radius:14px;
        overflow:hidden;
        display:block;
        width:100%;
        max-width:560px;
        margin:0 auto;
        position:relative;
        background: transparent;
      }
      #mate2-widget .layout{ display:flex; flex-direction:column; gap:14px; align-items:stretch; }
#mate2-widget .board-wrap{
  display:flex;
  justify-content:center;
  position:relative;
  width:100%;
  max-width:620px;
}



      #mate2-widget .board-wrap.mini-flash::before{
        content:"";
        position:absolute;
        inset:-2px;
        pointer-events:none;
        z-index:7;
        background:
          radial-gradient(380px 260px at 50% 40%, rgba(255,255,255,.42), rgba(255,255,255,0) 65%),
          linear-gradient(180deg, rgba(255,255,255,.28), rgba(255,255,255,0));
        animation: miniFlash .18s ease-out 1;
      }
      @keyframes miniFlash{
        0%{ opacity:0; transform: scale(.995); }
        35%{ opacity:1; transform: scale(1); }
        100%{ opacity:0; transform: scale(1.01); }
      }
      #mate2-widget .board-wrap.panic-screen::after{
        content:"";
        position:absolute;
        inset:0;
        pointer-events:none;
        z-index:2;
        background: radial-gradient(400px 300px at 50% 30%, rgba(255,255,255,.18), transparent 60%),
                    linear-gradient(180deg, rgba(255,0,80,.10), rgba(0,0,0,0));
        animation: panicScreen .55s linear infinite;
      }
      @keyframes panicScreen{
        0%,100%{ opacity:.15; }
        50%{ opacity:.35; }
      }

      #mate2-widget .mid-panel{ display:none; }
      #mate2-widget .right-panel{ display:flex; flex-direction:column; gap:14px; }

 @media (min-width: 900px){
  #mate2-widget .layout{
    display:grid;
    grid-template-columns: minmax(0, 1fr) minmax(360px, 460px);
    gap:20px;
    align-items:start;
  }


        #mate2-widget .board-wrap{ justify-content:flex-start; align-self:start; }
        #mate2-widget mini-chessboard{ max-width:100%; }

        #mate2-widget .mid-panel,
        #mate2-widget .right-panel{
          height: var(--board-h);
          min-height: var(--board-h);
          max-height: var(--board-h);
        }

        #mate2-widget .mid-panel{
          padding:16px;
          border:1px solid var(--bd);
          border-radius:16px;
          background:rgba(0,0,0,.02);
          box-shadow:0 10px 26px rgba(0,0,0,.06);
          overflow:auto;
        }
        @media (prefers-color-scheme: dark){
          #mate2-widget .mid-panel{
            background:rgba(255,255,255,.04);
            box-shadow:0 10px 26px rgba(0,0,0,.35);
          }
        }
        #mate2-widget .right-panel{ overflow:hidden; }
      }

      #mate2-widget .header{ display:flex; flex-direction:column; gap:10px; align-items:center; text-align:center; }
      @media (min-width: 900px){
        #mate2-widget .header{ align-items:flex-start; text-align:left; }
      }
      #mate2-widget .headline{
        font: 900 22px/1.05 var(--cr-font);
        text-transform:uppercase;
        padding:12px 18px;
        border-radius:999px;
        border:2px solid #000;
        background: linear-gradient(90deg, rgba(3,204,211,.22), rgba(244,0,99,.20));
        box-shadow:0 14px 34px rgba(0,0,0,.18);
      }
      #mate2-widget .subheadline{
        font: 900 18px/1.05 var(--cr-font);
        text-transform:uppercase;
        padding:11px 16px;
        border-radius:999px;
        border:2px solid #000;
        background: rgba(255,255,255,.92);
        box-shadow:0 14px 34px rgba(0,0,0,.12);
        color:#111827;
        text-shadow:none;
      }
      @media (prefers-color-scheme: dark){
        #mate2-widget .subheadline{
          background: rgba(255,255,255,.10);
          color:#fff;
          text-shadow:
            2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
            2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
            0 4px 0 rgba(0,0,0,.35);
        }
      }

      #mate2-widget .controls{ display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:10px; }

      #mate2-widget button{
        width:100%;
        padding:13px 14px;
        border-radius:14px;
        border:2px solid #000;
        background: linear-gradient(90deg, rgba(3,204,211,.18), rgba(244,0,99,.14));
        color:#fff;
        cursor:pointer;
        font: 900 16px/1 var(--cr-font);
        letter-spacing:.8px;
        text-transform:uppercase;
        transition:transform .06s ease, filter .12s ease, opacity .12s ease;
        box-shadow:0 10px 26px rgba(0,0,0,.14);
      }
      #mate2-widget button:hover{ filter:brightness(1.06); }
      #mate2-widget button:active{ transform:scale(.99); }
      #mate2-widget button:focus-visible{outline:3px solid var(--ring);outline-offset:2px}
      #mate2-widget button:disabled{ opacity:.55; cursor:not-allowed; }

      #mate2-widget .card{
        border:2px solid #000;
        border-radius:16px;
        padding:14px;
        box-shadow:0 12px 30px rgba(0,0,0,.14);
        background: linear-gradient(90deg, rgba(3,204,211,.18), rgba(244,0,99,.14));
        position:relative;
      }
      #mate2-widget .reward-title{ font: 900 18px/1.1 var(--cr-font); text-transform:uppercase; margin-bottom:10px; }
      #mate2-widget .reward-text{ font: 900 18px/1.2 var(--cr-font); text-transform:uppercase; margin-bottom:12px; }
      #mate2-widget .reward-actions{ display:flex; justify-content:center; gap:10px; flex-wrap:wrap; }
      @media (min-width: 900px){ #mate2-widget .reward-actions{ justify-content:flex-start; } }
      #mate2-widget .reward-btn{
        display:inline-flex; align-items:center; justify-content:center;
        padding:14px 18px;
        border-radius:16px;
        border:2px solid #000;
        background:#000;
        color:#fff;
        text-decoration:none;
        font:900 18px/1 var(--cr-font);
        text-transform:uppercase;
        box-shadow:0 14px 34px rgba(0,0,0,.25);
      }
      #mate2-widget .reward-btn.blink{ animation: rebelPulse 1s ease-in-out infinite; }
      @keyframes rebelPulse{
        0%, 100% { transform: scale(1); filter: brightness(1); }
        50%      { transform: scale(1.06); filter: brightness(1.25); }
      }


#mate2-widget .lb-right{
  border:4px solid #000;
  border-radius:22px;
  background:
    radial-gradient(380px 160px at 20% 0%, rgba(255,255,255,.10), transparent 60%),
    linear-gradient(180deg, #151515 0%, #050505 100%);
  color:#fff;
  box-shadow:
    0 10px 0 rgba(0,0,0,.18),
    0 18px 34px rgba(0,0,0,.16);
  padding:14px;
  position:relative;
  overflow:visible;
  width:100%;
  max-width:none;
}



#mate2-widget .lb-right::before{
  content:"";
  position:absolute;
  inset:-2px;
  background:linear-gradient(90deg, rgba(0,245,223,.22), rgba(244,0,99,.22));
  filter:blur(14px);
  opacity:.30;
  pointer-events:none;
}

      #mate2-widget .lb-inner{ position:relative; z-index:1; height:100%; display:flex; flex-direction:column; }
      #mate2-widget .lb-top{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:10px; flex-wrap:wrap; }
      #mate2-widget .lb-title{ font:900 16px/1.1 var(--cr-font); text-transform:uppercase; display:flex; align-items:center; gap:8px; }
      #mate2-widget .lb-title .dot{
        width:10px; height:10px; border-radius:999px;
        background: #15ff9a;
        box-shadow:0 0 0 3px rgba(21,255,154,.18), 0 0 18px rgba(21,255,154,.55);
      }
      #mate2-widget .lb-pill{
        border:2px solid rgba(255,255,255,.22);
        border-radius:999px;
        padding:8px 12px;
        background:rgba(255,255,255,.08);
        font:900 14px/1 var(--cr-font);
        text-transform:uppercase;
      }
#mate2-widget .lb-box{
  border:2px solid rgba(255,255,255,.14);
  border-radius:16px;
  padding:12px;
  background:rgba(255,255,255,.05);
  flex:1 1 auto;
  overflow:auto;
}

#mate2-widget .lb-box-title{
  font:900 14px/1.1 var(--cr-font);
  text-transform:uppercase;
  color:#fff;
  text-align:center;
  padding:10px 12px;
  margin-bottom:12px;
  border-radius:14px;
  border:3px solid #000;
  background:linear-gradient(180deg, #00f5df 0%, #F40063 100%);
  text-shadow:
    2px 0 #000, -2px 0 #000,
    0 2px #000, 0 -2px #000;
  box-shadow:0 5px 0 rgba(0,0,0,.18);
}

      #mate2-widget .lb-list{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px; }

#mate2-widget .lb-item{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;

  width:100%;
  padding:12px 16px;

  border-radius:999px;
  border:3px solid rgba(255,255,255,.26);
  background:rgba(255,255,255,.08);

  box-shadow:inset 0 0 0 1px rgba(255,255,255,.10);
}


#mate2-widget .lb-item.top1{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;

  width:100%;
  padding:12px 16px;

  border-radius:999px;
  border:3px solid #ffe44d;
  background:linear-gradient(90deg, rgba(0,245,223,.22), rgba(244,0,99,.22));

  box-shadow:inset 0 0 0 1px rgba(255,255,255,.10);
}





#mate2-widget .lb-left{
  flex:1 1 auto;
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}


#mate2-widget .lb-rightv{
  flex:0 0 auto;
  min-width:78px;
  margin-left:auto;
  text-align:right;
  white-space:nowrap;
  font:900 14px/1 var(--cr-font);
  color:#ffd84d;
}



      #mate2-widget .lb-muted{ opacity:.8; font:900 12px/1.2 var(--cr-font); text-transform:uppercase; margin-top:8px; }

      #mate2-widget .share-card{
        border:2px solid #0a0a0a;
        border-radius:16px;
        padding:12px 14px;
        background:linear-gradient(180deg, rgba(10,10,10,.92), rgba(0,0,0,.90));
        color:#e8f7ff;
        box-shadow: 0 14px 34px rgba(0,0,0,.18);
      }
      #mate2-widget .share-title{ font:900 16px/1.1 var(--cr-font); text-transform:uppercase; margin-bottom:10px; }
      #mate2-widget .share-btn{ border-radius:16px; border:2px solid rgba(255,255,255,.20); background:rgba(255,255,255,.08); }
      #mate2-widget .share-tip{
        margin-top:10px;
        font:900 12px/1.2 var(--cr-font);
        text-transform:uppercase;
        opacity:.9;
        display:flex;
        gap:8px;
        align-items:center;
      }
      #mate2-widget .ig-icon{ width:16px;height:16px;display:inline-block;flex:0 0 16px;opacity:.9; }

      #mate2-widget .hud{ position:absolute; inset:0; pointer-events:none; z-index:3; }
      #mate2-widget .hud-pill{
        position:absolute;
        top:10px;
        padding:10px 14px;
        border-radius:999px;
        background: var(--hud-bg);
        border:2px solid var(--hud-bd);
        box-shadow: var(--hud-shadow);
        display:flex;
        align-items:center;
        gap:10px;
        pointer-events:none;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        filter:none !important;
      }
      #mate2-widget .hud-dot{
        width:10px;
        height:10px;
        border-radius:999px;
        background:#00ff66;
        box-shadow:
          0 0 6px #00ff66,
          0 0 14px #00ff66,
          0 0 0 3px rgba(0,0,0,.35);
        opacity:1;
      }

      #mate2-widget #timerHudDot.orange{
        background:#ff9900;
        box-shadow:
          0 0 6px #ff9900,
          0 0 14px #ff9900,
          0 0 0 3px rgba(0,0,0,.35);
      }

      #mate2-widget #timerHudDot.red{
        background:#ff0033;
        box-shadow:
          0 0 6px #ff0033,
          0 0 14px #ff0033,
          0 0 0 3px rgba(0,0,0,.35);
      }

      #mate2-widget #timerHudDot.pulse{
        animation: timerDotPulse .6s ease-in-out infinite;
      }

      @keyframes timerDotPulse{
        0%, 100%{
          transform: scale(1);
          box-shadow:
            0 0 6px #ff0033,
            0 0 14px #ff0033,
            0 0 0 3px rgba(0,0,0,.35);
        }
        50%{
          transform: scale(1.35);
          box-shadow:
            0 0 10px #ff0033,
            0 0 24px #ff0033,
            0 0 38px rgba(255,0,51,.85),
            0 0 0 3px rgba(0,0,0,.35);
        }
      }

      #mate2-widget .hud-left{ left:10px; }
      #mate2-widget .hud-right{ right:10px; }

      #mate2-widget .hud-text{
        font: 900 22px/1 var(--cr-font);
        text-transform:uppercase;
        color:#fff;
        opacity: var(--hud-text-opacity);
        -webkit-text-stroke: 0 transparent;
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0  -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 4px 0 rgba(0,0,0,.30);
      }

      #mate2-widget.timer-white #timerHud .hud-text,
      #mate2-widget.timer-white #runHud .hud-text{
        color:#ffffff;
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 4px 0 rgba(0,0,0,.30);
      }

      #mate2-widget.timer-black #timerHud .hud-text,
      #mate2-widget.timer-black #runHud .hud-text{
        color:#111827;
        text-shadow:
          2px 0 #fff, -2px 0 #fff, 0 2px #fff, 0 -2px #fff,
          2px 2px #fff, -2px 2px #fff, 2px -2px #fff, -2px -2px #fff,
          0 4px 0 rgba(0,0,0,.20);
      }

      #mate2-widget .panic{ animation: panicFlash .55s linear infinite; }
      @keyframes panicFlash{
        0%,100%{ filter: brightness(1); transform: translateY(0); }
        50%{ filter: brightness(1.35); transform: translateY(-1px); }
      }

      #mate2-widget .combo{ animation: comboBounce .35s ease-out 1; }
      @keyframes comboBounce{
        0%{ transform: scale(1); }
        55%{ transform: scale(1.18); }
        100%{ transform: scale(1); }
      }

      #mate2-widget .board-overlay{
        position:absolute;
        inset:0;
        display:flex;
        align-items:center;
        justify-content:center;
        z-index:4;
        pointer-events:auto;
        isolation:isolate;
      }
      #mate2-widget .board-overlay[hidden]{ display:none !important; }

      #mate2-widget .overlay-card{
        width:min(360px, 52%);
        max-width:360px;
        border-radius:16px;
        border:2px solid rgba(255,255,255,.42);
        background: rgba(0,0,0,.62);
        box-shadow: 0 18px 46px rgba(0,0,0,.34);
        padding:14px 14px 16px;
        color:#fff;
      }
      @media (max-width:520px){
        #mate2-widget .overlay-card{ width:min(320px, 58%); }
      }
      #mate2-widget .overlay-title{
        text-align:center;
        margin-bottom:10px;
        font: 900 18px/1 var(--cr-font);
        text-transform:uppercase;
        color:#fff;
        -webkit-text-stroke:0 transparent;
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 4px 0 rgba(0,0,0,.35);
      }
      #mate2-widget .overlay-input{
        width:100%;
        padding:12px 14px;
        border-radius:999px;
        border:2px solid rgba(255,255,255,.38);
        background: rgba(255,255,255,.10);
        color:#fff;
        font: 900 16px/1 var(--cr-font);
        text-transform:uppercase;
        outline:none;
        box-sizing:border-box;
      }
      #mate2-widget .overlay-input::placeholder{ color: rgba(255,255,255,.85); }
      #mate2-widget .overlay-input:focus{
        outline:3px solid rgba(96,165,250,.85);
        outline-offset:2px;
      }
      #mate2-widget .overlay-btn{
        margin-top:10px;
        width:100%;
        padding:12px 14px;
        border-radius:999px;
        border:2px solid rgba(255,255,255,.38);
        background: rgba(255,255,255,.16);
        color:#fff;
        font: 900 18px/1 var(--cr-font);
        text-transform:uppercase;
        cursor:pointer;
      }

      #mate2-widget .center-toast{
        position:absolute;
        inset:0;
        display:flex;
        align-items:center;
        justify-content:center;
        z-index:6;
        pointer-events:none;
      }
      #mate2-widget .center-toast[hidden]{ display:none !important; }

      #mate2-widget .countdown-text{
        font: 900 86px/1 var(--cr-font);
        color:#fff;
        opacity:.92;
        -webkit-text-stroke:0 transparent;
        text-shadow:
          4px 0 #000, -4px 0 #000, 0 4px #000, 0 -4px #000,
          4px 4px #000, -4px 4px #000, 4px -4px #000, -4px -4px #000,
          0 10px 0 rgba(0,0,0,.35);
        text-transform:uppercase;
      }
      #mate2-widget .toast-sub{
        font: 900 40px/1.05 var(--cr-font);
        opacity:.90;
        text-transform:uppercase;
        text-align:center;
      }
      #mate2-widget.toast-white .toast-sub{
        color:#ffffff;
        text-shadow:
          3px 0 #000, -3px 0 #000, 0 3px #000, 0 -3px #000,
          3px 3px #000, -3px 3px #000, 3px -3px #000, -3px -3px #000,
          0 8px 0 rgba(0,0,0,.35);
      }
      #mate2-widget.toast-black .toast-sub{
        color:#111827;
        text-shadow:
          3px 0 #fff, -3px 0 #fff, 0 3px #fff, 0 -3px #fff,
          3px 3px #fff, -3px 3px #fff, 3px -3px #fff, -3px -3px #fff,
          0 8px 0 rgba(0,0,0,.25);
      }
      #mate2-widget .toast-pill{
        padding:0;
        border:none;
        background:transparent;
        box-shadow:none;
        border-radius:0;
        display:flex;
        flex-direction:column;
        align-items:center;
        gap:10px;
        pointer-events:none;
      }

      #mate2-widget .solve-pop{
        position:absolute;
        inset:0;
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:center;
        z-index:5;
        pointer-events:none;
        opacity:0;
        transform: scale(.92);
      }
      #mate2-widget .solve-pop.show{ animation: solvePop ${SOLVE_POP_MS}ms ease-out 1; }
      @keyframes solvePop{
        0%{ opacity:0; transform: scale(.92); }
        28%{ opacity:1; transform: scale(1.08); }
        100%{ opacity:0; transform: scale(1); }
      }
      #mate2-widget .checkmark{
        width:140px; height:140px;
        border-radius:999px;
        background: rgba(0,0,0,.20);
        border:3px solid rgba(255,255,255,.30);
        display:flex;
        align-items:center;
        justify-content:center;
        box-shadow: 0 20px 60px rgba(0,0,0,.25);
        position:relative;
      }
      #mate2-widget .checkmark svg{
        width:90px; height:90px;
        filter: drop-shadow(0 6px 0 rgba(0,0,0,.35));
      }
      #mate2-widget .plusone{
        margin-top:14px;
        font: 900 26px/1 var(--cr-font);
        color:#fff;
        opacity:.94;
        text-align:center;
        -webkit-text-stroke:0 transparent;
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 6px 0 rgba(0,0,0,.35);
      }
      #mate2-widget .solve-badge-chip{
        margin-top:12px;
        min-height:44px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        padding:10px 16px;
        border:4px solid #000;
        border-radius:16px;
        box-shadow:
          0 8px 0 rgba(0,0,0,.18),
          0 14px 24px rgba(0,0,0,.16);
        font:900 18px/1 var(--cr-font);
        text-transform:uppercase;
      }
      #mate2-widget .solve-badge-chip[hidden]{ display:none !important; }

      #mate2-widget .combo-pop{
        position:absolute;
        left:50%;
        top:10%;
        transform:translateX(-50%);
        z-index:6;
        pointer-events:none;
        opacity:0;
      }
      #mate2-widget .combo-pop.show{ animation: comboPopBurst 900ms ease-out 1; }
      #mate2-widget .combo-pop-inner{
        border:4px solid #000;
        border-radius:18px;
        padding:14px 18px;
        background:linear-gradient(180deg, #fff1a8 0%, #ff9f1c 100%);
        color:#fff;
        font:900 24px/1 var(--cr-font);
        text-transform:uppercase;
        white-space:nowrap;
        box-shadow:
          0 10px 0 rgba(0,0,0,.18),
          0 18px 34px rgba(0,0,0,.16);
        text-shadow:
          2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
          2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
          0 4px 0 rgba(0,0,0,.30);
      }
      @keyframes comboPopBurst{
        0%{ opacity:0; transform:translateX(-50%) translateY(18px) scale(.68) rotate(-4deg); }
        22%{ opacity:1; transform:translateX(-50%) translateY(0) scale(1.08) rotate(2deg); }
        70%{ opacity:1; transform:translateX(-50%) translateY(-8px) scale(1); }
        100%{ opacity:0; transform:translateX(-50%) translateY(-22px) scale(.96) rotate(0deg); }
      }

      #mate2-widget .slide-next{ animation: slideNext .28s ease-out 1; }
      @keyframes slideNext{
        0%{ transform: translateX(0); opacity:1; }
        100%{ transform: translateX(-18px); opacity:.0; }
      }

      /* =========================================================
         COMIC / SUPER-MARIO-STYLE MAKEOVER
      ========================================================= */

      #mate2-widget{
        --comic-yellow:#ffe44d;
        --comic-orange:#ff9f1c;
        --comic-red:#ff3b30;
        --comic-pink:#ff2d8f;
        --comic-cyan:#22d3ee;
        --comic-blue:#4f8cff;
        --comic-green:#33d17a;
        --comic-purple:#7c4dff;
        --comic-ink:#111111;
        --comic-white:#ffffff;
      }

      #mate2-widget .layout{
        gap:20px;
      }

@media (min-width: 900px){
  #mate2-widget .layout{
    display:grid;
    grid-template-columns: minmax(0, 1fr) minmax(360px, 460px);
    gap:20px;
    align-items:start;
  }



        #mate2-widget .mid-panel{
          padding:0;
          border:none;
          background:transparent;
          box-shadow:none;
          overflow:visible;
        }

        #mate2-widget .right-panel{
          gap:16px;
        }
      }

      #mate2-widget .board-wrap{
        filter: drop-shadow(0 18px 28px rgba(0,0,0,.18));
      }

#mate2-widget mini-chessboard{
  width:100%;
  max-width:620px;
  border:4px solid #000;
  border-radius:18px;
  box-shadow:
    0 14px 0 rgba(0,0,0,.18),
    0 24px 40px rgba(0,0,0,.18);
}




      /* ===== mid panels ===== */

      #mate2-widget .arcade-panel{
        position:relative;
        border:4px solid #000;
        border-radius:22px;
        padding:16px 14px 14px;
        box-shadow:
          0 10px 0 rgba(0,0,0,.18),
          0 18px 34px rgba(0,0,0,.16);
        overflow:hidden;
      }

      #mate2-widget .arcade-panel::before{
        content:"";
        position:absolute;
        inset:0;
        pointer-events:none;
        background:
          radial-gradient(320px 120px at 20% 0%, rgba(255,255,255,.35), transparent 60%),
          linear-gradient(180deg, rgba(255,255,255,.18), rgba(255,255,255,0));
      }

      #mate2-widget .panel-yellow{
        background:
          linear-gradient(180deg, #fff4a3 0%, #ffd84d 100%);
      }

      #mate2-widget .panel-cyan{
        background:
          linear-gradient(180deg, #c9f8ff 0%, #5ee8ff 100%);
      }

      #mate2-widget .panel-pink{
        background:
          linear-gradient(180deg, #ffd0e6 0%, #ff7ab8 100%);
      }

      #mate2-widget .panel-label{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        min-height:38px;
        padding:8px 14px;
        margin-bottom:14px;
        border:3px solid #000;
        border-radius:999px;
        background:#111;
        color:#fff;
        font:900 15px/1 var(--cr-font);
        letter-spacing:.8px;
        box-shadow:0 6px 0 rgba(0,0,0,.20);
      }

      /* ===== buttons ===== */

      #mate2-widget .controls.controls-mario{
        display:grid;
        grid-template-columns:1fr;
        gap:12px;
      }

      #mate2-widget .btn-comic{
        min-height:52px;
        border:4px solid #000 !important;
        border-radius:18px !important;
        box-shadow:
          0 8px 0 rgba(0,0,0,.18),
          0 14px 24px rgba(0,0,0,.12) !important;
        color:#fff !important;
        font:900 18px/1 var(--cr-font) !important;
        letter-spacing:.8px;
        transform:translateY(0);
      }

      #mate2-widget .btn-comic:hover{
        filter:brightness(1.05);
      }

      #mate2-widget .btn-comic:active{
        transform:translateY(3px);
        box-shadow:
          0 5px 0 rgba(0,0,0,.18),
          0 10px 16px rgba(0,0,0,.12) !important;
      }

#mate2-widget .btn-blue{
  background: linear-gradient(
    180deg,
    #00f5df 0%,
    #00cbb8 60%,
    #009e90 100%
  ) !important;
}


#mate2-widget .btn-pink{
background: linear-gradient(
180deg,
#F40063 0%,
#c80052 60%,
#9e003f 100%
) !important;
}
#mate2-widget .controls.controls-mario{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:12px;
}

#mate2-widget .controls.controls-mario .btn-comic{
  width:auto !important;
  min-width:220px;
  padding:0 28px !important;
  align-self:center;
}
@media (max-width: 899px){
  #mate2-widget .controls.controls-mario .btn-comic{
    min-width:200px;
    padding:0 24px !important;
  }
}

@media (max-width: 520px){
  #mate2-widget .controls.controls-mario .btn-comic{
    min-width:180px;
    padding:0 20px !important;
  }
}

      /* ===== stats ===== */

      #mate2-widget .stats-panel{
        margin-top:2px;
      }

      #mate2-widget .stats-grid{
        display:grid;
        grid-template-columns:1fr 1fr;
        gap:12px;
      }

      #mate2-widget .stat-box{
        border:4px solid #000;
        border-radius:18px;
        background:rgba(255,255,255,.78);
        padding:12px 10px 10px;
        box-shadow:0 8px 0 rgba(0,0,0,.14);
        text-align:center;
      }

      #mate2-widget .stat-kicker{
        color:#111;
        opacity:.95;
        margin-bottom:8px;
        display:block;
      }

      #mate2-widget .stat-value{
        color:#fff;
        font-size:28px;
        line-height:1;
      }

      #mate2-widget .puzzle-id-comic{
        display:block;
        margin-top:12px;
        color:#111;
        opacity:.9 !important;
      }

      /* ===== reward ===== */

      #mate2-widget .reward-panel{
        margin-top:2px;
      }

      #mate2-widget .reward-title{
        font:900 18px/1.05 var(--cr-font);
        margin-bottom:10px;
        color:#fff;
      }

      #mate2-widget .reward-text{
        font:900 24px/1.05 var(--cr-font);
        color:#fff;
        margin-bottom:14px;
      }

      #mate2-widget .btn-shop{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        min-height:58px;
        padding:0 20px;
        border-radius:18px;
        border:4px solid #000;
        background:linear-gradient(180deg, #111 0%, #000 100%);
        color:#fff;
        text-decoration:none;
        box-shadow:
          0 8px 0 rgba(0,0,0,.18),
          0 14px 24px rgba(0,0,0,.14);
      }

      #mate2-widget .btn-shop:active{
        transform:translateY(3px);
        box-shadow:
          0 5px 0 rgba(0,0,0,.18),
          0 10px 16px rgba(0,0,0,.14);
      }

      /* ===== leaderboard / share comic treatment ===== */


#mate2-widget .share-card{
  border:4px solid #000;
  border-radius:22px;
  padding:14px;
  background:
    radial-gradient(320px 120px at 10% 0%, rgba(255,255,255,.10), transparent 60%),
    linear-gradient(180deg, #151515 0%, #050505 100%);
  color:#fff;
  box-shadow:
    0 10px 0 rgba(0,0,0,.18),
    0 18px 34px rgba(0,0,0,.16);
}


      #mate2-widget .share-btn{
        min-height:54px;
        border:3px solid rgba(255,255,255,.22);
        border-radius:18px;
        box-shadow:0 7px 0 rgba(0,0,0,.16);
      }

      /* ===== overlay makeover ===== */

      #mate2-widget .overlay-card.overlay-card-arcade{
        width:min(430px, 72%);
        max-width:430px;
        border:4px solid #000;
        border-radius:24px;
        background:
          radial-gradient(380px 140px at 20% 0%, rgba(255,255,255,.26), transparent 60%),
          linear-gradient(180deg, #5be7ff 0%, #3da8ff 100%);
        box-shadow:
          0 12px 0 rgba(0,0,0,.18),
          0 24px 42px rgba(0,0,0,.24);
        padding:18px 16px 18px;
      }

      #mate2-widget .overlay-pill{
        display:flex;
        justify-content:center;
        align-items:center;
        min-height:44px;
        border:3px solid #000;
        border-radius:999px;
        padding:8px 14px;
        margin-bottom:10px;
        text-align:center;
        font:900 15px/1 var(--cr-font);
        text-transform:uppercase;
        box-shadow:0 6px 0 rgba(0,0,0,.18);
      }

      #mate2-widget .overlay-pill-yellow{
        background:linear-gradient(180deg, #fff1a8 0%, #ffd84d 100%);
        color:#111;
        text-shadow:none;
      }

      #mate2-widget .overlay-pill-pink{
        background:linear-gradient(180deg, #ffb0d9 0%, #ff4da0 100%);
        color:#fff;
      }

      #mate2-widget .overlay-title{
        margin:12px 0 12px;
        font:900 22px/1 var(--cr-font);
      }

      #mate2-widget .overlay-input{
        min-height:56px;
        border:4px solid #000;
        border-radius:18px;
        background:rgba(255,255,255,.94);
        color:#111;
        font:900 18px/1 var(--cr-font);
        text-shadow:none;
        box-shadow:0 8px 0 rgba(0,0,0,.16);
      }

      #mate2-widget .overlay-input::placeholder{
        color:#666;
      }

      #mate2-widget .overlay-btn.overlay-btn-arcade{
        min-height:58px;
        margin-top:14px;
        border:4px solid #000;
        border-radius:18px;
        background:linear-gradient(180deg, #9cff7f 0%, #2fd167 100%);
        color:#fff;
        box-shadow:
          0 8px 0 rgba(0,0,0,.18),
          0 14px 24px rgba(0,0,0,.12);
      }

      #mate2-widget .overlay-btn.overlay-btn-arcade:active{
        transform:translateY(3px);
        box-shadow:
          0 5px 0 rgba(0,0,0,.18),
          0 10px 16px rgba(0,0,0,.12);
      }

      /* ===== mobile ===== */

      @media (max-width: 899px){
        #mate2-widget .mid-panel{
          gap:14px;
        }

        #mate2-widget .stats-grid{
          grid-template-columns:1fr 1fr;
        }

        #mate2-widget .overlay-card.overlay-card-arcade{
          width:min(420px, 86%);
        }
      }

      /* ===== RUN STATS besser lesbar ===== */

      #mate2-widget .stat-box{
        background: rgba(255,255,255,.92);
      }

      #mate2-widget .stat-kicker{
        color:#ffffff !important;
        opacity:1 !important;
        text-shadow:
          1px 0 #000, -1px 0 #000, 0 1px #000, 0 -1px #000,
          1px 1px #000, -1px 1px #000, 1px -1px #000, -1px -1px #000;
      }

      #mate2-widget .puzzle-id-comic{
        color:#ffffff !important;
        opacity:1 !important;
        text-shadow:
          1px 0 #000, -1px 0 #000, 0 1px #000, 0 -1px #000,
          1px 1px #000, -1px 1px #000, 1px -1px #000, -1px -1px #000;
      }

      /* ===== MOBILE REWARD IMMER SICHTBAR ===== */

      @media (max-width: 899px){
        #mate2-widget #reward{
          position: fixed;
          left: 10px;
          right: 10px;
          bottom: max(10px, env(safe-area-inset-bottom));
          z-index: 9999;
          margin: 0;
        }

        #mate2-widget #reward .reward-panel{
          margin: 0 !important;
          padding: 12px 12px 14px !important;
          border-radius: 18px !important;
          box-shadow:
            0 10px 0 rgba(0,0,0,.18),
            0 18px 34px rgba(0,0,0,.24) !important;
        }

        #mate2-widget #reward .panel-label{
          margin-bottom: 8px !important;
        }

        #mate2-widget #reward .reward-title{
          font-size: 16px !important;
          margin-bottom: 6px !important;
        }

        #mate2-widget #reward .reward-text{
          font-size: 22px !important;
          margin-bottom: 10px !important;
        }

        #mate2-widget #reward .reward-actions{
          justify-content: center !important;
        }

        #mate2-widget #reward .btn-shop{
          width: 100%;
          min-height: 52px;
          font-size: 17px !important;
        }
      }

      /* ===== MOBILE HUD KLEINER ===== */

      @media (max-width: 899px){
        #mate2-widget .hud-pill{
          top: 6px;
          padding: 6px 8px;
          gap: 6px;
          border-width: 2px;
          border-radius: 999px;
        }

        #mate2-widget .hud-left{ left: 6px; }
        #mate2-widget .hud-right{ right: 6px; }

        #mate2-widget .hud-text{
          font-size: 17px;
          text-shadow:
            1px 0 #000, -1px 0 #000, 0 1px #000, 0 -1px #000,
            1px 1px #000, -1px 1px #000, 1px -1px #000, -1px -1px #000,
            0 3px 0 rgba(0,0,0,.25);
        }

        #mate2-widget.timer-black #timerHud .hud-text,
        #mate2-widget.timer-black #runHud .hud-text{
          text-shadow:
            1px 0 #fff, -1px 0 #fff, 0 1px #fff, 0 -1px #fff,
            1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff,
            0 3px 0 rgba(0,0,0,.18);
        }

        #mate2-widget .hud-dot{
          width: 8px;
          height: 8px;
          box-shadow: 0 0 0 2px rgba(0,0,0,.20);
        }
      }

      @media (max-width: 520px){
        #mate2-widget .hud-pill{
          top: 4px;
          padding: 5px 7px;
          gap: 5px;
        }

        #mate2-widget .hud-left{ left: 4px; }
        #mate2-widget .hud-right{ right: 4px; }

        #mate2-widget .hud-text{
          font-size: 15px;
        }

        #mate2-widget .hud-dot{
          width: 7px;
          height: 7px;
        }
      }

      /* ===== MOBILE ARCADE FINE-TUNING ===== */

      @media (max-width: 899px){
        #mate2-widget{
          margin: 8px auto;
          padding: 8px;
          border-radius: 18px;
        }

        #mate2-widget .layout{
          gap: 10px;
        }

        #mate2-widget .board-wrap{
          margin: 0;
        }

        #mate2-widget mini-chessboard{
          max-width: 100%;
          border-width: 3px;
          border-radius: 16px;
          box-shadow:
            0 6px 0 rgba(0,0,0,.18),
            0 14px 24px rgba(0,0,0,.18);
        }

        #mate2-widget .mid-panel,
        #mate2-widget .right-panel{
          gap: 10px;
        }

        #mate2-widget .panel,
        #mate2-widget .lb-right,
        #mate2-widget .share-card,
        #mate2-widget .card,
        #mate2-widget .reward-panel{
          border-radius: 18px !important;
          box-shadow:
            0 6px 0 rgba(0,0,0,.16),
            0 12px 20px rgba(0,0,0,.14) !important;
        }

        #mate2-widget .panel{
          padding: 10px !important;
        }

        #mate2-widget .panel-label,
        #mate2-widget .lb-pill{
          font-size: 12px !important;
          padding: 6px 10px !important;
        }

        #mate2-widget button,
        #mate2-widget .btn-blue,
        #mate2-widget .btn-pink,
        #mate2-widget .btn-green,
        #mate2-widget .share-btn,
        #mate2-widget .reward-btn,
        #mate2-widget .btn-shop{
          min-height: 48px;
          font-size: 16px !important;
          border-radius: 16px !important;
        }

        #mate2-widget .controls{
          gap: 8px;
        }

        #mate2-widget .lb-top{
          gap: 8px;
          margin-bottom: 8px;
        }

        #mate2-widget .lb-box{
          padding: 8px !important;
        }

        #mate2-widget .lb-box-title{
          font-size: 12px !important;
          margin-bottom: 8px !important;
        }





#mate2-widget .lb-muted{
  opacity:.9;
  font:900 12px/1.2 var(--cr-font);
  text-transform:uppercase;
  margin-top:10px;
  color:#fff;
}


        #mate2-widget .share-title{
          font-size: 14px !important;
          margin-bottom: 8px !important;
        }

        #mate2-widget .share-tip{
          font-size: 10px !important;
          gap: 6px;
          margin-top: 8px !important;
        }

        #mate2-widget .ig-icon{
          width: 13px;
          height: 13px;
          flex: 0 0 13px;
        }

        #mate2-widget .solve-pop .checkmark{
          width: 104px;
          height: 104px;
        }

        #mate2-widget .solve-pop .checkmark svg{
          width: 68px;
          height: 68px;
        }

        #mate2-widget .plusone{
          font-size: 20px;
          margin-top: 10px;
        }

        #mate2-widget .solve-badge-chip{
          min-height: 36px;
          font-size: 14px;
          padding: 8px 12px;
          margin-top: 10px;
        }

        #mate2-widget .combo-pop-inner{
          font-size: 18px;
          padding: 10px 14px;
          border-radius: 14px;
        }

        #mate2-widget .overlay-card{
          width: min(92%, 360px);
          padding: 12px 12px 14px;
          border-radius: 18px;
        }

        #mate2-widget .overlay-title{
          font-size: 16px !important;
          margin-bottom: 8px !important;
        }

        #mate2-widget .overlay-input{
          min-height: 46px;
          font-size: 15px !important;
          padding: 10px 12px !important;
        }

        #mate2-widget .overlay-btn{
          min-height: 48px;
          font-size: 17px !important;
          margin-top: 8px !important;
        }

        #mate2-widget .countdown-text{
          font-size: 64px !important;
        }

        #mate2-widget .toast-sub{
          font-size: 28px !important;
        }
      }

      @media (max-width: 520px){
        #mate2-widget{
          margin: 6px auto;
          padding: 6px;
          border-radius: 16px;
        }

        #mate2-widget .layout{
          gap: 8px;
        }

        #mate2-widget mini-chessboard{
          border-radius: 14px;
        }

        #mate2-widget .panel,
        #mate2-widget .lb-right,
        #mate2-widget .share-card,
        #mate2-widget .card,
        #mate2-widget .reward-panel{
          border-radius: 16px !important;
        }

        #mate2-widget .panel{
          padding: 8px !important;
        }

        #mate2-widget button,
        #mate2-widget .btn-blue,
        #mate2-widget .btn-pink,
        #mate2-widget .btn-green,
        #mate2-widget .share-btn,
        #mate2-widget .reward-btn,
        #mate2-widget .btn-shop{
          min-height: 44px;
          font-size: 14px !important;
          border-radius: 14px !important;
        }

        #mate2-widget .panel-label,
        #mate2-widget .lb-pill{
          font-size: 11px !important;
          padding: 5px 9px !important;
        }





        #mate2-widget .share-title{
          font-size: 13px !important;
        }

        #mate2-widget .share-tip{
          font-size: 9px !important;
        }

        #mate2-widget .overlay-card{
          width: min(94%, 340px);
          padding: 10px 10px 12px;
        }

        #mate2-widget .overlay-title{
          font-size: 15px !important;
        }

        #mate2-widget .overlay-input{
          min-height: 42px;
          font-size: 14px !important;
        }

        #mate2-widget .overlay-btn{
          min-height: 44px;
          font-size: 15px !important;
        }

        #mate2-widget .countdown-text{
          font-size: 52px !important;
        }

        #mate2-widget .toast-sub{
          font-size: 22px !important;
        }

        #mate2-widget .solve-pop .checkmark{
          width: 90px;
          height: 90px;
        }

        #mate2-widget .solve-pop .checkmark svg{
          width: 58px;
          height: 58px;
        }

        #mate2-widget .plusone{
          font-size: 18px;
        }

        #mate2-widget .solve-badge-chip{
          font-size: 12px;
          min-height: 32px;
          padding: 7px 10px;
        }

        #mate2-widget .combo-pop-inner{
          font-size: 15px;
          padding: 9px 12px;
        }
      }

      /* ===== MOBILE PANELS KOMPAKTER ===== */

      @media (max-width: 899px){
        #mate2-widget .mid-panel{
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        #mate2-widget .right-panel{
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        #mate2-widget .lb-inner{
          gap: 8px;
        }
      }

      .reward-badges{
        display:flex;
        gap:8px;
        margin-top:8px;
        flex-wrap:wrap;
      }

      .reward-badge{
        padding:6px 10px;
        border-radius:12px;
        border:2px solid #000;
        font:900 12px/1 var(--cr-font);
        text-transform:uppercase;
      }

      .badge-bronze{
        background:linear-gradient(#e0a46b,#a85a2a);
      }

      .badge-silver{
        background:linear-gradient(#f3f4f6,#9ca3af);
        color:#111;
      }

      .badge-gold{
        background:linear-gradient(#ffe27a,#ffb800);
        color:#111;
      }

/* ===== CLEANER START OVERLAY ===== */

/* ===== CLEANER START OVERLAY ===== */

#mate2-widget .overlay-card.overlay-card-clean{
  width:min(360px, 84%);
  max-width:360px;

  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:14px;

  border-radius:20px;
  border:2px solid #0a0a0a;

  background:
    radial-gradient(1200px 800px at 20% -30%, rgba(0,245,223,.18), transparent 60%),
    radial-gradient(900px 700px at 120% 10%, rgba(244,0,99,.18), transparent 55%),
    linear-gradient(180deg, rgba(18,18,18,.96), rgba(0,0,0,.94));

  color:#e8f7ff;

  box-shadow:
    0 18px 40px rgba(0,0,0,.35),
    inset 0 0 0 1px rgba(255,255,255,.06);

  padding:22px 20px;
  text-align:center;
}

#mate2-widget .overlay-mini-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;

  min-height:36px;
  padding:8px 16px;
  margin:0 auto;

  border:2px solid #000;
  border-radius:999px;

  background:linear-gradient(90deg, #00f5df, #F40063);
  color:#fff;
  font-weight:900;
  font-size:12px;
  line-height:1;
  text-align:center;

  box-shadow:0 6px 0 rgba(0,0,0,.18);
}

#mate2-widget .overlay-subtitle{
  width:100%;
  max-width:260px;
  margin:0 auto;

  text-align:center;
  color:#fff;
  font-size:14px;
  line-height:1.35;
  opacity:.98;
}

#mate2-widget .overlay-input.overlay-input-clean{
  min-height:52px;
  width:min(260px, 100%);
  margin:0 auto;

  border:4px solid #000;
  border-radius:16px;
  background:#f3f4f6;
  color:#111;

  font:900 17px/1 var(--cr-font);
  text-transform:uppercase;
  text-align:left;
  text-shadow:none;

  box-shadow:0 6px 0 rgba(0,0,0,.14);
  padding:10px 14px;
  box-sizing:border-box;
}

#mate2-widget .overlay-input.overlay-input-clean::placeholder{
  color:#6b7280;
}

#mate2-widget .overlay-input.overlay-input-clean:focus{
  outline:3px solid rgba(96,165,250,.95);
  outline-offset:2px;
}

/* WICHTIG: hier war vorher dein Tippfehler */
#mate2-widget .overlay-btn.overlay-btn-clean{
  display:inline-flex;
  align-items:center;
  justify-content:center;

  min-height:50px;
  width:auto;
  min-width:170px;
  margin:2px auto 0;
  padding:0 28px;

  border:2px solid rgba(255,255,255,.16);
  border-radius:14px;

  background:
    linear-gradient(180deg, rgba(32,32,32,1) 0%, rgba(10,10,10,1) 100%);
  color:#fff;

  box-shadow:
    0 6px 0 #000,
    0 10px 18px rgba(0,0,0,.35),
    inset 0 1px 0 rgba(255,255,255,.08);

  font:900 18px/1 var(--cr-font);
  letter-spacing:.6px;
  text-transform:uppercase;
  text-align:center;

  position:relative;
  overflow:hidden;
}

#mate2-widget .overlay-btn.overlay-btn-clean::before{
  content:"";
  position:absolute;
  inset:0;
  border-radius:inherit;
  padding:2px;
  background:linear-gradient(90deg, rgba(0,245,223,.95), rgba(244,0,99,.95));
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;
  mask-composite:exclude;
  pointer-events:none;
}

#mate2-widget .overlay-btn.overlay-btn-clean:hover{
  filter:brightness(1.08);
  transform:translateY(-1px);
}

#mate2-widget .overlay-btn.overlay-btn-clean:active{
  transform:translateY(3px);
  box-shadow:
    0 3px 0 #000,
    0 7px 14px rgba(0,0,0,.30);
}






/* optional: alter pinker/gelber promo-look im Startscreen nicht mehr so dominant */
#mate2-widget .overlay-card.overlay-card-clean .overlay-pill{
  display:none;
}

@media (max-width: 899px){
  #mate2-widget .overlay-title.overlay-title-clean{
    font-size:18px;
  }

  #mate2-widget .overlay-subtitle{
    font-size:12px;
    max-width:230px;
  }

  #mate2-widget .overlay-input.overlay-input-clean{
    min-height:48px;
    font-size:15px;
    border-radius:14px;
  }

  #mate2-widget .overlay-btn.overlay-btn-clean{
    min-height:50px;
    min-width:150px;
    padding:0 24px;
    font-size:16px;
    border-radius:14px;
  }
}

@media (max-width: 899px){
  #mate2-widget .overlay-card.overlay-card-clean{
    width:min(82%, 320px);
    padding:16px 12px 14px;
    gap:10px;
    border-radius:18px;
  }

  #mate2-widget .overlay-mini-badge{
    min-height:32px;
    padding:6px 12px;
    font-size:11px;
  }

  #mate2-widget .overlay-subtitle{
    font-size:11px;
    max-width:220px;
    line-height:1.3;
  }

  #mate2-widget .overlay-input.overlay-input-clean{
    min-height:42px;
    width:min(220px, 100%);
    font-size:14px;
    padding:8px 10px;
    border-radius:12px;
  }

  #mate2-widget .overlay-btn.overlay-btn-clean{
    min-height:42px;
    min-width:130px;
    padding:0 20px;
    font-size:14px;
    border-radius:12px;
  }
}

@media (max-width: 520px){
  #mate2-widget .overlay-card.overlay-card-clean{
    width:min(80%, 290px);
    padding:14px 10px 12px;
    gap:8px;
    border-radius:16px;
  }

  #mate2-widget .overlay-mini-badge{
    min-height:28px;
    padding:5px 10px;
    font-size:10px;
  }

  #mate2-widget .overlay-subtitle{
    font-size:10px;
    max-width:190px;
    line-height:1.25;
  }

  #mate2-widget .overlay-input.overlay-input-clean{
    min-height:38px;
    width:min(200px, 100%);
    font-size:12px;
    padding:7px 9px;
  }

  #mate2-widget .overlay-btn.overlay-btn-clean{
    min-height:38px;
    min-width:115px;
    padding:0 16px;
    font-size:13px;
  }
}


@media (max-width: 520px){
  #mate2-widget .overlay-card.overlay-card-clean{
    width:min(80%, 290px);
    padding:12px 10px 10px;
    border-radius:16px;
    gap:7px;
  }

  #mate2-widget .overlay-mini-badge{
    min-height:28px;
    padding:5px 10px;
    font-size:10px;
  }

  #mate2-widget .overlay-title.overlay-title-clean{
    font-size:14px;
  }

  #mate2-widget .overlay-subtitle{
    font-size:9px;
    max-width:170px;
  }

  #mate2-widget .overlay-input.overlay-input-clean{
    min-height:36px;
    width:min(200px, 100%);
    font-size:12px;
    padding:7px 9px;
  }

  #mate2-widget .overlay-btn.overlay-btn-clean{
    min-height:38px;
    min-width:110px;
    padding:0 16px;
    font-size:13px;
  }
}

  #mate2-widget .overlay-mini-badge{
    min-height:34px;
    padding:7px 14px;
    font-size:12px;
  }

  #mate2-widget .overlay-title.overlay-title-clean{
    font-size:16px;
  }

  #mate2-widget .overlay-subtitle{
    font-size:11px;
    max-width:210px;
  }

  #mate2-widget .overlay-input.overlay-input-clean{
    min-height:44px;
    font-size:14px;
    padding:9px 12px;
  }

#mate2-widget .overlay.btn.overlay-btn-clean{
  border-radius:14px;
  border:2px solid rgba(255,255,255,.25);

  background:linear-gradient(180deg, rgba(30,30,30,1), rgba(10,10,10,1));
  color:#fff;

  box-shadow:
    0 6px 0 #000,
    0 10px 18px rgba(0,0,0,.35);
}


}

@media (max-width: 899px){
  #mate2-widget .board-overlay{
    align-items:flex-start;
    justify-content:center;
    padding-top:16px;
    padding-bottom:16px;
  }

  #mate2-widget .overlay-card.overlay-card-endgame{
    max-height:calc(100dvh - 32px);
    overflow-y:auto;
    -webkit-overflow-scrolling:touch;
  }
}

@media (max-width: 520px){

  /* Stats Boxen kleiner */
  #mate2-widget .endgame-stats{
    gap:10px;
  }

  #mate2-widget .endgame-stat{
    padding:10px;
  }

  #mate2-widget .endgame-stat-title{
    font-size:12px;
  }

  #mate2-widget .endgame-stat-value{
    font-size:18px;
  }

  /* Badges kompakter */
  #mate2-widget .endgame-badges{
    gap:8px;
  }

  #mate2-widget .reward-badge{
    padding:6px 10px;
    font-size:12px;
  }

  /* Buttons kompakter */
  #mate2-widget .endgame-actions .btn-comic{
    min-height:42px;
    font-size:14px;
  }

}


/* ===== END SCREEN SHOP BUTTON ===== */

#mate2-widget #endShopBtn{
  display:flex;
  align-items:center;
  justify-content:center;
  width:100%;
  min-height:74px;
  padding:0 24px;

  border:2px solid rgba(255,255,255,.14);
  border-radius:22px;
  background:linear-gradient(180deg, #2c2c2c 0%, #0f0f0f 100%);
  color:#fff;
  text-decoration:none;

  font:900 24px/1 var(--cr-font);
  text-transform:uppercase;
  letter-spacing:.5px;

  box-shadow:
    0 8px 0 #000,
    0 14px 24px rgba(0,0,0,.18),
    inset 0 1px 0 rgba(255,255,255,.08);

  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000;

  position:relative;
  overflow:visible;
}

#mate2-widget #endShopBtn::before{
  content:"";
  position:absolute;
  inset:0;
  border-radius:inherit;
  padding:2px;
  background:linear-gradient(90deg, #00f5df, #F40063);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;
  mask-composite:exclude;
  pointer-events:none;
}



#mate2-widget #endShopBtn:active{
  transform:translateY(3px);
  box-shadow:
    0 5px 0 rgba(0,0,0,.18),
    0 10px 16px rgba(0,0,0,.14);
}

/* ===== END SCREEN COIN BADGE ===== */

#mate2-widget .endgame-stat-value.coin-value{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;

  background:linear-gradient(#fff6b3,#ffd84d);
  border:3px solid #000;
  border-radius:12px;

  padding:6px 12px;

  font-size:20px;
  color:#fff;

  box-shadow:0 5px 0 rgba(0,0,0,.15);
}

/* ===== END SCREEN BADGES ===== */

#mate2-widget .end-badge-icon{
  width:70px;
  height:70px;

  display:flex;
  align-items:center;
  justify-content:center;

  border:4px solid #000;
  border-radius:16px;

  font-size:36px;

  box-shadow:0 6px 0 rgba(0,0,0,.18);
}

#mate2-widget .end-badge-icon.badge-bronze{
  background:linear-gradient(#e0a46b,#a85a2a);
}

#mate2-widget .end-badge-icon.badge-silver{
  background:linear-gradient(#f3f4f6,#9ca3af);
}

#mate2-widget .end-badge-icon.badge-gold{
  background:linear-gradient(#ffe27a,#ffb800);
}

/* ===== CLEAN BOARD ACTIONS ===== */

#mate2-widget .board-actions{
  display:flex;
  justify-content:center;
  align-items:center;
  gap:12px;
  margin-top:4px;
  flex-wrap:wrap;
  width:100%;
}


#mate2-widget .board-actions .btn-comic{
  width:auto !important;
  min-width:130px;
  min-height:40px;
  padding:0 16px !important;
  font-size:13px !important;
  border-radius:14px !important;
  box-shadow:
    0 6px 0 rgba(0,0,0,.18),
    0 10px 18px rgba(0,0,0,.12) !important;
}


@media (max-width: 520px){
  #mate2-widget .board-actions{
    gap:10px;
    margin-top:10px;
  }

  #mate2-widget .board-actions .btn-comic{
    min-width:120px;
    min-height:40px;
    padding:0 14px !important;
    font-size:13px !important;
  }
}

#mate2-widget .left-panel{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:12px;
  min-width:0;
  width:100%;
}



#mate2-widget .right-panel{
  width:100%;
  display:flex;
  flex-direction:column;
  gap:14px;
  align-self:start;
}

@media (min-width: 900px){
  #mate2-widget .right-panel{
    width:100%;
    max-width:none;
  }
}



/* ===== DESKTOP BOARD SIZE ===== */

@media (min-width: 900px){

  #mate2-widget .left-panel{
    max-width:620px;
  }

  #mate2-widget mini-chessboard{
    max-width:620px;
  }

}

@media (max-width: 899px){
  #mate2-widget .right-panel{
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:10px;
  }

  #mate2-widget .lb-right,
  #mate2-widget .share-card{
    width:100%;
    max-width:none;
    margin-left:auto;
    margin-right:auto;
  }
}


#mate2-widget{
  width:100%;
  box-sizing:border-box;
}

#mate2-widget *{
  box-sizing:border-box;
}
@media (max-width: 520px){
  #mate2-widget .overlay-card.overlay-card-endgame{
    width:min(90%, 320px);
    max-height:calc(100dvh - 24px);
    padding:12px 10px;
    gap:10px;
    border-radius:16px;
  }

#mate2-widget .endgame-stats{
  background:rgba(255,255,255,.08);
  border:2px solid rgba(255,255,255,.18);
  border-radius:16px;
  padding:10px 14px;
}


  #mate2-widget .endgame-stat{
    min-height:72px;
    padding:8px 6px;
    border-radius:14px;
  }

  #mate2-widget .endgame-stat-value{
    font-size:20px;
  }

  #mate2-widget .reward-badge{
    padding:5px 8px;
    font-size:11px;
  }

  #mate2-widget #endShopBtn,
  #mate2-widget .endgame-replay-btn{
    min-height:40px;
    min-width:160px;
    font-size:13px;
    padding:0 14px;
  }
}






/* ===== SIMPLE END OVERLAY ===== */

#mate2-widget #endOverlay.board-overlay{
  position:absolute;
  inset:0;
  z-index:20;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:14px;
  pointer-events:auto;
}

#mate2-widget .overlay-card.overlay-card-end-simple{
  width:min(84%, 520px);
  max-width:520px;
  min-height:auto;
  aspect-ratio:1 / 1;

  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:22px;

  padding:28px 22px;
  border:4px solid #000;
  border-radius:28px;

  background:
    radial-gradient(320px 140px at 20% 0%, rgba(255,255,255,.18), transparent 60%),
    linear-gradient(180deg, #6ad9ff 0%, #4aaeff 100%);

  box-shadow:
    0 12px 0 rgba(0,0,0,.18),
    0 24px 42px rgba(0,0,0,.22);

  overflow:hidden;
}

#mate2-widget .end-simple-title{
  text-align:center;
  font:900 34px/1 var(--cr-font);
  color:#fff;
  text-transform:uppercase;
  letter-spacing:.6px;
  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
    0 6px 0 rgba(0,0,0,.28);
}

#mate2-widget .end-simple-stats{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:20px;
  flex-wrap:wrap;
  text-align:center;
}

#mate2-widget .end-simple-stat{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  min-height:36px;
}

#mate2-widget .end-simple-icon{
  font-size:28px;
  line-height:1;
}

#mate2-widget .end-simple-value{
  font:900 28px/1 var(--cr-font);
  color:#fff;
  text-transform:uppercase;
  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000;
}

#mate2-widget #endBadgesInline{
  gap:10px;
}

#mate2-widget .end-simple-badge{
  width:38px;
  height:38px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border:3px solid #000;
  border-radius:12px;
  font-size:20px;
  box-shadow:0 5px 0 rgba(0,0,0,.16);
}

#mate2-widget .end-simple-badge.badge-bronze{
  background:linear-gradient(#e0a46b,#a85a2a);
}

#mate2-widget .end-simple-badge.badge-silver{
  background:linear-gradient(#f3f4f6,#9ca3af);
}

#mate2-widget .end-simple-badge.badge-gold{
  background:linear-gradient(#ffe27a,#ffb800);
}

#mate2-widget .end-simple-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  text-decoration:none;

  min-width:260px;
  height:58px;
  padding:0 24px;

  border:4px solid #000;
  border-radius:18px;

  font:900 22px/1 var(--cr-font);
  letter-spacing:.5px;
  text-transform:uppercase;

  box-shadow:
    0 8px 0 rgba(0,0,0,.18),
    0 14px 24px rgba(0,0,0,.14);
}

#mate2-widget .end-simple-btn-dark{
  background:linear-gradient(180deg, #111 0%, #000 100%);
  color:#fff;
}

#mate2-widget .end-simple-btn-green{
  background:linear-gradient(180deg, #9cff7f 0%, #35d96f 100%);
  color:#fff;
}

#mate2-widget .end-simple-btn:active{
  transform:translateY(3px);
  box-shadow:
    0 5px 0 rgba(0,0,0,.18),
    0 10px 16px rgba(0,0,0,.12);
}

#mate2-widget .end-simple-btn-dark.blink{
  animation: rebelPulse 1s ease-in-out infinite;
}

@media (max-width: 899px){
  #mate2-widget .overlay-card.overlay-card-end-simple{
    width:min(86%, 430px);
    gap:18px;
    padding:22px 16px;
    border-radius:22px;
  }

  #mate2-widget .end-simple-title{
    font-size:26px;
  }

  #mate2-widget .end-simple-stats{
    gap:14px;
  }

  #mate2-widget .end-simple-icon{
    font-size:22px;
  }

  #mate2-widget .end-simple-value{
    font-size:22px;
  }

  #mate2-widget .end-simple-badge{
    width:34px;
    height:34px;
    font-size:18px;
    border-radius:10px;
  }

  #mate2-widget .end-simple-btn{
    min-width:210px;
    height:48px;
    font-size:17px;
    border-radius:14px;
  }

#mate2-widget .end-simple-btn-green{
  min-width:170px !important;
}


}

@media (max-width: 520px){
  #mate2-widget #endOverlay.board-overlay{
    padding:10px;
  }

  #mate2-widget .overlay-card.overlay-card-end-simple{
    width:min(88%, 340px);
    gap:14px;
    padding:18px 12px;
    border-radius:18px;
  }

  #mate2-widget .end-simple-title{
    font-size:22px;
  }

  #mate2-widget .end-simple-stats{
    gap:10px;
  }

  #mate2-widget .end-simple-stat{
    gap:6px;
  }

  #mate2-widget .end-simple-icon{
    font-size:18px;
  }

  #mate2-widget .end-simple-value{
    font-size:18px;
  }

  #mate2-widget .end-simple-badge{
    width:28px;
    height:28px;
    font-size:15px;
    border-width:2px;
    border-radius:8px;
  }

  #mate2-widget .end-simple-btn{
    min-width:180px;
    height:42px;
    font-size:14px;
    padding:0 16px;
    border-radius:12px;
  }

#mate2-widget .end-simple-btn-green{
  min-width:150px !important;
}


}

#mate2-widget .end-simple-title-pill{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:12px;

  min-height:56px;
  padding:10px 22px;

  border:4px solid #000;
  border-radius:999px;
  background:linear-gradient(180deg, #fff1a8 0%, #ffd84d 100%);
  color:#111;

  box-shadow:
    0 8px 0 rgba(0,0,0,.18),
    0 14px 24px rgba(0,0,0,.14);

  font:900 28px/1 var(--cr-font);
  text-transform:uppercase;
  letter-spacing:.5px;
  text-shadow:none;
}

#mate2-widget .end-simple-flag{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:22px;
  line-height:1;
}

#mate2-widget .end-simple-stats{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:18px;
  flex-wrap:wrap;
  text-align:center;
}

#mate2-widget .end-simple-results-label{
  font:900 22px/1 var(--cr-font);
  color:#fff;
  text-transform:uppercase;
  letter-spacing:.5px;
  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000;
}

#mate2-widget .end-simple-icon{
  font-size:28px;
  line-height:1;
}

#mate2-widget .end-simple-icon-puzzle{
  filter: drop-shadow(0 2px 0 rgba(0,0,0,.18));
}

#mate2-widget .end-simple-btn-green{
  min-width:180px !important;
  width:auto !important;
}

@media (max-width: 899px){
  #mate2-widget .end-simple-title-pill{
    min-height:46px;
    padding:8px 18px;
    font-size:22px;
    gap:10px;
  }

  #mate2-widget .end-simple-flag{
    font-size:18px;
  }

  #mate2-widget .end-simple-results-label{
    font-size:18px;
  }
}

@media (max-width: 520px){
  #mate2-widget .end-simple-title-pill{
    min-height:40px;
    padding:7px 14px;
    font-size:18px;
    gap:8px;
    border-width:3px;
  }

  #mate2-widget .end-simple-flag{
    font-size:15px;
  }

  #mate2-widget .end-simple-results-label{
    font-size:14px;
  }
}

/* ===== END OVERLAY RESULTS PILL ===== */

#mate2-widget .end-results-pill{
  width:min(92%, 520px);
  display:flex;
  align-items:center;
  justify-content:center;
  flex-wrap:wrap;
  gap:12px;

  padding:12px 14px;
  border:4px solid #000;
  border-radius:20px;
  background:rgba(255,255,255,.18);
  box-shadow:
    0 8px 0 rgba(0,0,0,.16),
    0 14px 24px rgba(0,0,0,.12);
}

#mate2-widget .end-results-label{
  white-space:nowrap;
}

#mate2-widget .end-result-item{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  min-height:40px;
}

#mate2-widget .end-result-icon{
  font-size:26px;
  line-height:1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}

#mate2-widget .end-result-value{
  font-size:28px;
  line-height:1;
  white-space:nowrap;
}

#mate2-widget .end-result-badges{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  flex-wrap:wrap;
}

#mate2-widget .end-result-badges .reward-badge{
  margin:0;
  padding:5px 9px;
  font-size:10px;
  border-radius:10px;
}

@media (max-width: 899px){
  #mate2-widget .end-results-pill{
    gap:10px;
    padding:10px 12px;
    border-radius:18px;
  }

  #mate2-widget .end-result-icon{
    font-size:22px;
  }

  #mate2-widget .end-result-value{
    font-size:22px;
  }
}

@media (max-width: 520px){
  #mate2-widget .end-results-pill{
    width:min(94%, 330px);
    gap:8px;
    padding:8px 10px;
    border-radius:16px;
  }

  #mate2-widget .end-results-label{
    font-size:10px;
  }

  #mate2-widget .end-result-item{
    gap:4px;
    min-height:32px;
  }

  #mate2-widget .end-result-icon{
    font-size:18px;
  }

  #mate2-widget .end-result-value{
    font-size:18px;
  }

  #mate2-widget .end-result-badges .reward-badge{
    padding:4px 7px;
    font-size:9px;
  }
}

#mate2-widget #endShopBtn.blink{
  animation: rebelPulse 1s ease-in-out infinite;
}

@media (min-width: 900px){
  #mate2-widget .right-panel{
    width:100%;
    max-width:none;
  }

  #mate2-widget .lb-right,
  #mate2-widget .share-card{
    width:100%;
    max-width:none;
  }
}

/* ===== NEW CLEAN END OVERLAY ===== */

#mate2-widget #endOverlay.board-overlay{
  position:absolute;
  inset:0;
  z-index:20;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:14px;
  pointer-events:auto;
}

#mate2-widget .overlay-card.overlay-card-end-rebels{
  width:min(84%, 520px);
  max-width:520px;

  display:flex;
  flex-direction:column;
  align-items:center;
  gap:22px;

  padding:28px 22px 22px;
  border:2px solid #0a0a0a;
  border-radius:28px;

  background:
    radial-gradient(1200px 800px at 20% -30%, rgba(0,245,223,.16), transparent 60%),
    radial-gradient(900px 700px at 120% 10%, rgba(244,0,99,.16), transparent 55%),
    linear-gradient(180deg, rgba(18,18,18,.97), rgba(5,5,5,.94));

  color:#fff;

  box-shadow:
    0 18px 40px rgba(0,0,0,.38),
    inset 0 0 0 1px rgba(255,255,255,.05);
}


#mate2-widget .end-rebels-title{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height:52px;
  padding:8px 22px;

  border:2px solid rgba(255,255,255,.14);
  border-radius:999px;
  background:linear-gradient(180deg, #2a2a2a 0%, #0e0e0e 100%);
  color:#fff;

  box-shadow:
    0 6px 0 #000,
    0 12px 22px rgba(0,0,0,.30),
    inset 0 1px 0 rgba(255,255,255,.08);

  font:900 26px/1 var(--cr-font);
  text-transform:uppercase;
  letter-spacing:.4px;
}



#mate2-widget .end-rebels-results{
  width:100%;
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:14px;

  padding:18px 16px;
  border:2px solid rgba(255,255,255,.12);
  border-radius:24px;
  background:rgba(255,255,255,.04);

  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,.04),
    0 10px 20px rgba(0,0,0,.24);
}



#mate2-widget .end-rebels-stat{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:10px;
  min-height:112px;
  text-align:center;
}

#mate2-widget .end-rebels-stat-label{
  font:900 16px/1 var(--cr-font);
  color:#fff;
  text-transform:uppercase;
  letter-spacing:.4px;
}

#mate2-widget .end-rebels-stat-value{
  font:900 40px/1 var(--cr-font);
  color:#fff;
  text-transform:uppercase;
  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000,
    0 6px 0 rgba(0,0,0,.26);
}

#mate2-widget .end-rebels-shop{
  display:flex;
  align-items:center;
  justify-content:center;
  width:100%;
  min-height:74px;
  padding:0 24px;

  border:2px solid rgba(255,255,255,.14);
  border-radius:22px;
  background:linear-gradient(180deg, #2c2c2c 0%, #0f0f0f 100%);
  color:#fff;
  text-decoration:none;

  font:900 24px/1 var(--cr-font);
  text-transform:uppercase;
  letter-spacing:.5px;

  box-shadow:
    0 8px 0 #000,
    0 14px 24px rgba(0,0,0,.18),
    inset 0 1px 0 rgba(255,255,255,.08);

  text-shadow:
    2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
    2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000;

  position:relative;
  overflow:hidden;
}

#mate2-widget .end-rebels-shop::before{
  content:"";
  position:absolute;
  inset:0;
  border-radius:inherit;
  padding:2px;
  background:linear-gradient(90deg, #00f5df, #F40063);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;
  mask-composite:exclude;
  pointer-events:none;
}



#mate2-widget .end-rebels-shop.blink{
  animation: rebelPulse 1s ease-in-out infinite;
}

#mate2-widget .end-rebels-bottom{
  width:100%;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:16px;
}

#mate2-widget .end-rebels-secondary{
  min-height:72px;
  width:100%;
  padding:0 18px;

  border:2px solid rgba(255,255,255,.14);
  border-radius:22px;
  background:linear-gradient(180deg, #2a2a2a 0%, #111 100%);
  color:#fff;

  font:900 22px/1 var(--cr-font);
  text-transform:uppercase;
  letter-spacing:.5px;

  box-shadow:
    0 8px 0 #000,
    0 14px 24px rgba(0,0,0,.18),
    inset 0 1px 0 rgba(255,255,255,.06);
}


#mate2-widget .end-rebels-secondary:active,
#mate2-widget .end-rebels-shop:active{
  transform:translateY(3px);
  box-shadow:
    0 5px 0 rgba(0,0,0,.18),
    0 10px 16px rgba(0,0,0,.12);
}

@media (max-width: 899px){
  #mate2-widget .overlay-card.overlay-card-end-rebels{
    width:min(88%, 430px);
    gap:18px;
    padding:22px 16px 18px;
    border-radius:22px;
  }

  #mate2-widget .end-rebels-title{
    min-height:48px;
    padding:8px 18px;
    font-size:22px;
  }

  #mate2-widget .end-rebels-results{
    gap:10px;
    padding:14px 10px;
    border-radius:18px;
  }

  #mate2-widget .end-rebels-stat{
    min-height:90px;
    gap:8px;
  }

  #mate2-widget .end-rebels-stat-label{
    font-size:12px;
  }

  #mate2-widget .end-rebels-stat-value{
    font-size:28px;
  }

  #mate2-widget .end-rebels-shop{
    min-height:60px;
    font-size:18px;
    border-radius:18px;
  }

  #mate2-widget .end-rebels-bottom{
    gap:12px;
  }

  #mate2-widget .end-rebels-secondary{
    min-height:54px;
    font-size:17px;
    border-radius:16px;
  }
}

@media (max-width: 520px){
  #mate2-widget #endOverlay.board-overlay{
    padding:10px;
  }

  #mate2-widget .overlay-card.overlay-card-end-rebels{
    width:min(90%, 340px);
    gap:14px;
    padding:18px 12px 14px;
    border-radius:18px;
  }

  #mate2-widget .end-rebels-title{
    min-height:42px;
    padding:7px 14px;
    font-size:18px;
    border-width:3px;
  }

  #mate2-widget .end-rebels-results{
    gap:8px;
    padding:10px 8px;
    border-width:3px;
    border-radius:16px;
  }

  #mate2-widget .end-rebels-stat{
    min-height:74px;
    gap:6px;
  }

  #mate2-widget .end-rebels-stat-label{
    font-size:10px;
  }

  #mate2-widget .end-rebels-stat-value{
    font-size:20px;
  }

  #mate2-widget .end-rebels-shop{
    min-height:46px;
    font-size:14px;
    border-width:3px;
    border-radius:14px;
  }

  #mate2-widget .end-rebels-bottom{
    gap:10px;
  }

  #mate2-widget .end-rebels-secondary{
    min-height:42px;
    font-size:14px;
    border-width:3px;
    border-radius:14px;
  }
}

    </style>

    <div class="layout">
<div class="left-panel">
      <div class="board-wrap" id="boardWrap">
        <mini-chessboard
          id="board"
          role="application"
          aria-label="Chessboard. Click or drag pieces to input moves."
          data-watermark="https://cdn.shopify.com/s/files/1/0895/6103/9184/files/chessrebels_logo_4096_borderless_final.png?v=1769886689"
          data-watermark-size="var(--wm-size)"
          data-watermark-opacity="var(--wm-opacity)"></mini-chessboard>

        <div class="hud">
          <div class="hud-pill hud-left" id="runHud">
            <span class="hud-dot"></span>
            <span class="hud-text" id="runHudText">🪙 0</span>
          </div>

          <div class="hud-pill hud-right" id="timerHud">
            <span class="hud-dot" id="timerHudDot"></span>
            <span class="hud-text" id="timerHudText">03:00</span>
          </div>
        </div>

        <div class="center-toast" id="centerToast" hidden>
          <div class="toast-pill">
            <div class="countdown-text" id="toastBig">3</div>
            <div class="toast-sub" id="toastSub" hidden>WHITE TO MOVE</div>
          </div>
        </div>

        <div class="combo-pop" id="comboPop" hidden>
          <div class="combo-pop-inner arcade" id="comboPopText">SPEED BONUS x2</div>
        </div>

      <div class="board-overlay" id="startOverlay" hidden>
  <div class="overlay-card overlay-card-clean">
    <div class="overlay-mini-badge arcade">⚡ 3-MIN CHALLENGE ⚡</div>

    <div class="overlay-subtitle arcade small">
      SOLVE PUZZLES TO UNLOCK REWARDS!
    </div>

    <input
      id="overlayName"
      class="overlay-input overlay-input-clean"
      type="text"
      maxlength="18"
      inputmode="text"
      placeholder="PLAYER NAME"
      aria-label="Player name"
    >

    <button id="overlayStart" class="overlay-btn overlay-btn-clean arcade" type="button">
      START
    </button>
  </div>
</div>

<div class="board-overlay" id="endOverlay" hidden>
  <div class="overlay-card overlay-card-end-rebels">
    <div class="end-rebels-title arcade">RUN COMPLETE</div>

    <div class="end-rebels-results">
      <div class="end-rebels-stat">
        <div class="end-rebels-stat-label arcade small">PUZZLES</div>
        <div class="end-rebels-stat-value arcade" id="endSolvedValue">0</div>
      </div>

      <div class="end-rebels-stat">
        <div class="end-rebels-stat-label arcade small">COINS</div>
        <div class="end-rebels-stat-value arcade" id="endCoinsValue">0</div>
      </div>

      <div class="end-rebels-stat">
        <div class="end-rebels-stat-label arcade small">BADGE</div>
        <div class="end-rebels-stat-value arcade" id="endBadgeTierIcon">—</div>
      </div>
    </div>

    <a id="endShopBtn" class="end-rebels-shop arcade" href="#" hidden>
      SHOP NOW
    </a>

    <div class="end-rebels-bottom">
      <button id="overlayPlayAgain" class="end-rebels-secondary arcade" type="button">
        PLAY AGAIN
      </button>

      <button id="endShareBtn" class="end-rebels-secondary arcade" type="button">
        SHARE
      </button>
    </div>
  </div>
</div>





        <div class="solve-pop" id="solvePop" aria-hidden="true">
          <div class="checkmark">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path fill="#22c55e" d="M26 46 12 32l5-5 9 9 21-21 5 5-26 26z"/>
            </svg>
          </div>
          <div class="solve-badge-chip arcade" id="solveBadgeChip" hidden>🥉 BRONZE</div>
          <div class="plusone" id="solveGainText">+ 1 REBEL COIN 🪙</div>
        </div>
      </div>
<div class="board-actions">
  <button id="hintBtn" type="button" class="arcade btn-comic btn-blue">HINT</button>
  <button id="solveBtn" type="button" class="arcade btn-comic btn-pink">SHOW SOLUTION</button>
</div>
</div>

   <div class="mid-panel" aria-label="Puzzle controls panel"></div>

      <div class="right-panel">
        <div id="globalBoard" class="lb-right" aria-live="polite">
          <div class="lb-inner">
            <div class="lb-top">
              <div class="lb-title arcade"><span class="dot"></span> LEADERBOARD</div>
            </div>

            <div class="lb-box">
              <div class="lb-box-title arcade">GLOBAL ALL-TIME TOP 5</div>
              <ol class="lb-list" id="globalTopList">
                <li class="lb-item"><span class="lb-left">LOADING…</span><span class="lb-rightv">--</span></li>
              </ol>
              <div class="lb-muted arcade small" id="globalTopMeta">UPDATED: --</div>
            </div>
          </div>
        </div>

        <div id="shareBottom" class="share-card">
          <div class="share-title arcade">SHARE YOUR STATS</div>
          <button class="share-btn arcade" id="shareBtn" type="button">SHARE</button>
          <div class="share-tip arcade small">
            <svg class="ig-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9Zm4.5 4.2a3.8 3.8 0 1 1 0 7.6 3.8 3.8 0 0 1 0-7.6Zm0 2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Zm5-2.35a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/>
            </svg>
            <span>TAG US — <strong>@CHESSREBELS_OFFICIAL</strong></span>
          </div>
        </div>
      </div>
    </div>
  </div>
  `;

  function mount() {
    const mountEl = document.getElementById(MOUNT_ID);
    if (!mountEl) return;
    if (mountEl.__crMounted) return;
    mountEl.__crMounted = true;
    mountEl.innerHTML = HTML;

    const root = mountEl.querySelector("#mate2-widget");
    if (!root) return;

    if (!customElements.get("mini-chessboard")) {
      class MiniChessboard extends HTMLElement {
        constructor() {
          super();
          this.attachShadow({ mode: "open" });
          this.pos = {};
          this.selected = null;
          this.flipped = false;
          this.hintSquares = [];

          this.dragFrom = null;
          this.dragActive = false;

          this.pieceMap = new Map();

          const style = `
            :host{ display:block; aspect-ratio:1/1; --piece-scale: 1.46; }
            .wrap{ position:relative; width:100%; height:100%; touch-action:none; }
            .board{
              position:relative; width:100%; height:100%;
              display:grid; grid-template-columns:repeat(8,1fr); grid-template-rows:repeat(8,1fr);
              z-index:0;
            }
            .board::after{
              content:""; position:absolute; left:50%; top:50%;
              transform:translate(-50%,-50%); width:var(--wm-size,60%); height:var(--wm-size,60%);
              background-image:var(--wm, none); background-size:contain; background-position:center; background-repeat:no-repeat;
              opacity:var(--wm-opacity,.12); pointer-events:none; z-index:1;
            }
            .sq{ position:relative; user-select:none; overflow:hidden; z-index:0; }
            .light{ background:var(--sq-light,#03CCD3); }
            .dark{ background:var(--sq-dark,#F40063); }
            .sq.selected{ outline:3px solid var(--ring,#60a5fa); outline-offset:-3px; }
            .sq.hint-from{ box-shadow:inset 0 0 0 3px rgba(99,102,241,.9); }
            .sq.hint-to{ box-shadow:inset 0 0 0 3px rgba(34,197,94,.9); }

            .coord{ position:absolute; font:10px system-ui; opacity:.55; color:var(--fg,#111827); }
            .coord.file{ right:4px; bottom:2px; } .coord.rank{ left:4px; top:2px; }

            .pieces{ position:absolute; inset:0; z-index:2; pointer-events:auto; }

            .piece{
              position:absolute;
              width:12.5%;
              height:12.5%;
              left:0; top:0;
              padding:2.4%;
              box-sizing:border-box;
              transform: translate(var(--tx, 0%), var(--ty, 0%)) scale(var(--piece-scale, 1));
              transform-origin: 50% 50%;
              will-change: transform;
              user-select:none;
              -webkit-user-drag:none;
              object-fit:contain;
              cursor:grab;
              pointer-events:auto;
            }
            .piece:active{ cursor:grabbing; }
            .piece.anim{ transition: transform ${PIECE_GLIDE_MS}ms cubic-bezier(.2,.85,.2,1); }

            .fx{ position:absolute; inset:0; z-index:3; pointer-events:none; }
            .fx-explode{
              position:absolute;
              width:12.5%;
              height:12.5%;
              left:0;
              top:0;
              transform: translate(var(--tx,0%), var(--ty,0%));
              transform-origin:50% 50%;
              pointer-events:none;
              contain:layout paint;
            }
            .fx-explode .boom{
              position:absolute;
              left:50%;
              top:50%;
              width:92%;
              height:92%;
              transform:translate(-50%,-50%) scale(.28);
              border-radius:999px;
              background:
                radial-gradient(circle at 50% 50%,
                  #fff200 0%,
                  #ffd24a 18%,
                  #ffb300 34%,
                  #ff7a00 52%,
                  #ff2a00 70%,
                  rgba(255,0,0,0) 100%);
              opacity:0;
              animation: crBoom ${Math.round(260 * EXPLODE_SCALE)}ms ease-out 1;
              box-shadow:
                0 0 26px rgba(255,210,0,.95),
                0 0 56px rgba(255,120,0,.78),
                0 0 88px rgba(255,40,0,.66);
              filter: drop-shadow(0 6px 0 rgba(0,0,0,.34));
            }
            .fx-explode .sparks{
              position:absolute;
              left:50%;
              top:50%;
              width:10px;
              height:10px;
              margin-left:-5px;
              margin-top:-5px;
              border-radius:999px;
              background:#fff200;
              box-shadow:
                24px 0 0 #ff6a00,
                -24px 0 0 #ff6a00,
                0 24px 0 #ff2a00,
                0 -24px 0 #ff2a00,
                19px 19px 0 #ffb300,
                -19px 19px 0 #ffb300,
                19px -19px 0 #ffb300,
                -19px -19px 0 #ffb300,
                0 32px 0 rgba(255,40,0,.85),
                32px 0 0 rgba(255,160,0,.85),
                -32px 0 0 rgba(255,160,0,.85),
                0 -32px 0 rgba(255,40,0,.85);
              opacity:0;
              transform:scale(.34);
              animation: crSparks ${Math.round(320 * EXPLODE_SCALE)}ms ease-out 1;
              filter: drop-shadow(0 0 14px rgba(255,130,0,.95));
            }
            .fx-explode .smoke{
              position:absolute;
              left:50%;
              top:50%;
              width:170%;
              height:170%;
              transform:translate(-50%,-50%) scale(.48);
              opacity:0;
              background:
                radial-gradient(circle at 35% 60%, rgba(70,70,70,.40), transparent 60%),
                radial-gradient(circle at 62% 45%, rgba(45,45,45,.40), transparent 60%),
                radial-gradient(circle at 50% 50%, rgba(65,65,65,.32), transparent 66%),
                radial-gradient(circle at 40% 38%, rgba(55,55,55,.25), transparent 68%),
                radial-gradient(circle at 70% 70%, rgba(45,45,45,.24), transparent 70%);
              filter: blur(.25px);
              animation: crSmoke ${Math.round(620 * EXPLODE_SCALE)}ms ease-out 1;
            }
            @keyframes crBoom{
              0%   { opacity:0; transform:translate(-50%,-50%) scale(.18); }
              18%  { opacity:1; }
              100% { opacity:0; transform:translate(-50%,-50%) scale(1.55); }
            }
            @keyframes crSparks{
              0%   { opacity:0; transform:scale(.28); }
              26%  { opacity:1; }
              100% { opacity:0; transform:scale(1.45); }
            }
            @keyframes crSmoke{
              0%   { opacity:0; transform:translate(-50%,-50%) scale(.45); }
              22%  { opacity:.95; }
              100% { opacity:0; transform:translate(-50%,-62%) scale(1.55); }
            }

            @media (max-width:520px){
              :host{ --piece-scale: 1.44; }
              .piece{ padding:2.8%; }
              .piece.anim{ transition-duration: ${PIECE_GLIDE_MS_MOBILE}ms; }
            }
            @media (prefers-reduced-motion: reduce){
              .piece.anim{ transition: none !important; }
              .fx-explode *{ animation:none !important; opacity:0 !important; }
            }
          `;

          this.shadowRoot.innerHTML =
            `<style>${style}</style>
             <div class="wrap" tabindex="0" aria-label="Chessboard">
               <div class="board"></div>
               <div class="fx"></div>
               <div class="pieces"></div>
             </div>`;

          this.root = this.shadowRoot.querySelector(".wrap");
          this.boardEl = this.shadowRoot.querySelector(".board");
          this.fxEl = this.shadowRoot.querySelector(".fx");
          this.piecesEl = this.shadowRoot.querySelector(".pieces");

          this.onKey = this.onKey.bind(this);
          this.onPointerDown = this.onPointerDown.bind(this);
          this.onPointerUp = this.onPointerUp.bind(this);
        }

        connectedCallback() {
          const url = this.getAttribute("data-watermark");
          const size = this.getAttribute("data-watermark-size") || "60%";
          const opacity = this.getAttribute("data-watermark-opacity") || ".12";
          if (url) this.style.setProperty("--wm", `url("${url}")`);
          this.style.setProperty("--wm-size", size);
          this.style.setProperty("--wm-opacity", opacity);

          this.root.addEventListener("keydown", this.onKey);
          this.root.addEventListener("pointerdown", this.onPointerDown, { passive: false });
          this.root.addEventListener("pointerup", this.onPointerUp, { passive: false });

          this.renderSquares();
          this.rebuildPieces();
        }

        disconnectedCallback() {
          this.root.removeEventListener("keydown", this.onKey);
          this.root.removeEventListener("pointerdown", this.onPointerDown);
          this.root.removeEventListener("pointerup", this.onPointerUp);
        }

        static get observedAttributes() {
          return ["data-watermark", "data-watermark-size", "data-watermark-opacity"];
        }

        attributeChangedCallback(name, _o, v) {
          if (name === "data-watermark" && v) this.style.setProperty("--wm", `url("${v}")`);
          if (name === "data-watermark-size") this.style.setProperty("--wm-size", v || "60%");
          if (name === "data-watermark-opacity") this.style.setProperty("--wm-opacity", v || ".12");
        }

        setPosition(newPos, lastMove) {
          this.pos = { ...newPos };
          this.renderSquares();

          if (!lastMove || !lastMove.from || !lastMove.to) {
            this.rebuildPieces();
            return;
          }

          const from = lastMove.from;
          const to = lastMove.to;

          const moverEl = this.pieceMap.get(from);
          if (!moverEl) {
            this.rebuildPieces();
            return;
          }

          const capturedEl = this.pieceMap.get(to);
          if (capturedEl && capturedEl !== moverEl) {
            try { capturedEl.remove(); } catch {}
            this.pieceMap.delete(to);
          }

          this.pieceMap.delete(from);
          this.pieceMap.set(to, moverEl);

          moverEl.classList.add("anim");
          this.setPieceSquare(moverEl, to);

          const newCode = this.pos[to];
          if (newCode) {
            moverEl.dataset.piece = newCode;
            moverEl.src = this.pieceSrc(newCode);
            moverEl.alt = newCode;
          }

          this.reconcilePiecesSnap();
        }

        explodeSquare(sq) {
          if (!this.fxEl || !sq) return;
          const fx = document.createElement("div");
          fx.className = "fx-explode";
          const { x, y } = this.squareToGrid(sq);
          fx.style.setProperty("--tx", `${x * 100}%`);
          fx.style.setProperty("--ty", `${y * 100}%`);
          fx.innerHTML = `<div class="boom"></div><div class="sparks"></div><div class="smoke"></div>`;
          this.fxEl.appendChild(fx);

          const removeAfter = Math.round(760 * EXPLODE_SCALE);
          setTimeout(() => { try { fx.remove(); } catch {} }, removeAfter);
        }

        highlight(square) { this.selected = square || null; this.renderSquares(); }
        setHints(from, to) { this.hintSquares = [from, to].filter(Boolean); this.renderSquares(); }
        clearHints() { this.hintSquares = []; this.renderSquares(); }
        setFlipped(v) { this.flipped = !!v; this.renderSquares(); this.rebuildPieces(); }

        pieceSrc(code) {
          const url = PIECE_URLS[code];
          if (url && /^https?:\/\//.test(url)) return url;
          const isW = code && code[0] === "w";
          const F = isW ? "#fff" : "#111827", S = isW ? "#111827" : "#e5e7eb";
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><g fill="${F}" stroke="${S}" stroke-width="3"><circle cx="50" cy="50" r="40"/></g></svg>`;
          return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
        }

        squareToGrid(sq) {
          const file = sq[0];
          const rank = parseInt(sq[1], 10);
          const fIdx = "abcdefgh".indexOf(file);
          if (fIdx < 0 || !rank) return { x: 0, y: 0 };
          if (this.flipped) return { x: 7 - fIdx, y: rank - 1 };
          return { x: fIdx, y: 8 - rank };
        }

        gridToSquare(x, y) {
          if (this.flipped) {
            const file = "hgfedcba"[x] || "a";
            const rank = String(y + 1);
            return file + rank;
          }
          const file = "abcdefgh"[x] || "a";
          const rank = String(8 - y);
          return file + rank;
        }

        squareFromPoint(clientX, clientY) {
          const rect = this.boardEl.getBoundingClientRect();
          const px = clientX - rect.left;
          const py = clientY - rect.top;
          if (px < 0 || py < 0 || px > rect.width || py > rect.height) return null;
          const cellW = rect.width / 8;
          const cellH = rect.height / 8;
          const x = Math.max(0, Math.min(7, Math.floor(px / cellW)));
          const y = Math.max(0, Math.min(7, Math.floor(py / cellH)));
          return this.gridToSquare(x, y);
        }

        setPieceSquare(el, sq) {
          const { x, y } = this.squareToGrid(sq);
          el.style.setProperty("--tx", `${x * 100}%`);
          el.style.setProperty("--ty", `${y * 100}%`);
          el.dataset.sq = sq;
        }

        gridIndexToSquare(i) {
          const r = Math.floor(i / 8), f = i % 8;
          const rank = this.flipped ? r + 1 : 8 - r;
          const file = this.flipped ? "hgfedcba"[f] : "abcdefgh"[f];
          return file + rank;
        }

        onPointerDown(e) {
          if (e.pointerType === "touch") e.preventDefault();
          const sq = this.squareFromPoint(e.clientX, e.clientY);
          if (!sq) return;

          const hasPiece = !!this.pos[sq];
          if (hasPiece) {
            this.dragFrom = sq;
            this.dragActive = true;
            try { this.root.setPointerCapture(e.pointerId); } catch {}
          }
          this.dispatchEvent(new CustomEvent("squareclick", { detail: { square: sq }, bubbles: true }));
        }

        onPointerUp(e) {
          if (!this.dragActive) return;
          const toSq = this.squareFromPoint(e.clientX, e.clientY);
          const fromSq = this.dragFrom;
          this.dragActive = false;
          this.dragFrom = null;
          try { this.root.releasePointerCapture(e.pointerId); } catch {}
          if (fromSq && toSq && fromSq !== toSq) {
            this.dispatchEvent(new CustomEvent("pieceMove", { detail: { from: fromSq, to: toSq }, bubbles: true }));
          }
        }

        renderSquares() {
          const b = this.boardEl;
          b.innerHTML = "";
          for (let r = 0; r < 8; r++) {
            for (let f = 0; f < 8; f++) {
              const idx = r * 8 + f;
              const sq = this.gridIndexToSquare(idx);
              const color = ((this.flipped ? 7 - r : r) + f) % 2 === 0 ? "light" : "dark";

              const div = document.createElement("div");
              div.className = `sq ${color}`;
              div.dataset.sq = sq;
              if (this.selected === sq) div.classList.add("selected");
              if (this.hintSquares[0] === sq) div.classList.add("hint-from");
              if (this.hintSquares[1] === sq) div.classList.add("hint-to");

              const showRank = this.flipped ? f === 7 : f === 0;
              const showFile = this.flipped ? r === 0 : r === 7;
              if (showRank) {
                const rank = document.createElement("div");
                rank.className = "coord rank";
                rank.textContent = sq[1];
                div.appendChild(rank);
              }
              if (showFile) {
                const file = document.createElement("div");
                file.className = "coord file";
                file.textContent = sq[0];
                div.appendChild(file);
              }

              b.appendChild(div);
            }
          }
        }

        rebuildPieces() {
          this.piecesEl.innerHTML = "";
          this.pieceMap.clear();

          for (const [sq, code] of Object.entries(this.pos || {})) {
            const img = document.createElement("img");
            img.className = "piece";
            img.draggable = false;
            img.alt = code;
            img.referrerPolicy = "no-referrer";
            img.dataset.piece = code;
            img.src = this.pieceSrc(code);
            this.setPieceSquare(img, sq);
            this.piecesEl.appendChild(img);
            this.pieceMap.set(sq, img);
          }

          requestAnimationFrame(() => {
            for (const el of this.piecesEl.querySelectorAll(".piece")) el.classList.add("anim");
          });
        }

        reconcilePiecesSnap() {
          const desired = new Set(Object.keys(this.pos || {}));

          for (const [sq, el] of Array.from(this.pieceMap.entries())) {
            if (!desired.has(sq)) {
              try { el.remove(); } catch {}
              this.pieceMap.delete(sq);
            }
          }

          for (const sq of desired) {
            const code = this.pos[sq];
            const el = this.pieceMap.get(sq);
            if (!el) {
              const img = document.createElement("img");
              img.className = "piece anim";
              img.draggable = false;
              img.alt = code;
              img.referrerPolicy = "no-referrer";
              img.dataset.piece = code;
              img.src = this.pieceSrc(code);
              this.setPieceSquare(img, sq);
              this.piecesEl.appendChild(img);
              this.pieceMap.set(sq, img);
              continue;
            }
            this.setPieceSquare(el, sq);
            if (el.dataset.piece !== code) {
              el.dataset.piece = code;
              el.src = this.pieceSrc(code);
              el.alt = code;
            }
          }
        }

        onKey(e) {
          const squares = [...this.boardEl.querySelectorAll(".sq")];
          if (!squares.length) return;
          let i = this.selected ? squares.findIndex((n) => n.dataset.sq === this.selected) : 0;
          const col = i % 8, row = Math.floor(i / 8);
          if (e.key === "ArrowRight") { i = row * 8 + Math.min(7, col + 1); e.preventDefault(); }
          else if (e.key === "ArrowLeft") { i = row * 8 + Math.max(0, col - 1); e.preventDefault(); }
          else if (e.key === "ArrowUp") { i = Math.max(0, i - 8); e.preventDefault(); }
          else if (e.key === "ArrowDown") { i = Math.min(63, i + 8); e.preventDefault(); }
          else if (e.key === "Enter" || e.key === " ") {
            const sq = squares[i].dataset.sq;
            this.dispatchEvent(new CustomEvent("squareclick", { detail: { square: sq }, bubbles: true }));
            e.preventDefault();
            return;
          } else return;
          this.highlight(squares[i].dataset.sq);
        }
      }
      customElements.define("mini-chessboard", MiniChessboard);
    }

    initWidget(root);
  }

  /* ========= HELPERS ========= */
  function getBerlinDate() {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Berlin" }));
  }
  function ymd(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  function getISOWeekKey(dateObj) {
    const d = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    const isoYear = d.getUTCFullYear();
    return `${isoYear}-W${String(weekNo).padStart(2, "0")}`;
  }
  function pad2(n) { return String(n).padStart(2, "0"); }
  function fmtMMSS(sec) {
    sec = Math.max(0, Math.floor(sec));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${pad2(m)}:${pad2(s)}`;
  }
  function escapeHtml_(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function splitCSV(line) {
    const out = [];
    let cur = "", q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { q = !q; continue; }
      if (c === "," && !q) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out;
  }

async function loadPuzzles() {
  try {
    const txt = await fetch(DATA_URL, { cache: "no-store" }).then((r) => {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.text();
    });

    const cleanTxt = txt.replace(/^\uFEFF/, ""); // BOM entfernen
    const lines = cleanTxt.trim().split(/\r?\n/);

    const header = splitCSV(lines.shift()).map(h => h.trim());
    const idx = {
      PuzzleId: header.indexOf("PuzzleId"),
      FEN: header.indexOf("FEN"),
      Moves: header.indexOf("Moves"),
      Themes: header.indexOf("Themes"),
      StartMode: header.indexOf("StartMode"),
    };

    if (idx.PuzzleId < 0 || idx.FEN < 0 || idx.Moves < 0 || idx.Themes < 0) {
      throw new Error("CSV header not recognized: " + JSON.stringify(header));
    }

    const list = [];

    for (const line of lines) {
      if (!line.trim()) continue;

      const cols = splitCSV(line);

      const themes = (cols[idx.Themes] || "").trim();
      if (!themes.includes("mateIn3")) continue;

      const id = (cols[idx.PuzzleId] || "").trim();
      const fen = (cols[idx.FEN] || "").trim();
      const moves = (cols[idx.Moves] || "").trim();
      const startMode = idx.StartMode >= 0 ? (cols[idx.StartMode] || "").trim().toLowerCase() : "";

      if (id && fen && moves) {
        list.push({ id, fen, moves, startMode });
      }

      if (list.length >= 1000) break;
    }

    if (!list.length) throw new Error("no mateIn3 parsed from CSV");

    localStorage.setItem(LS_KEY, JSON.stringify(list));
    return list;
  } catch (e) {
    console.error("loadPuzzles failed:", e);
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
      return cached.length ? cached : FALLBACKS;
    } catch {
      return FALLBACKS;
    }
  }
}


  function parseFEN(fen) {
    const parts = fen.split(" ");
    const rows = parts[0].split("/");
    const sideToMove = parts[1];
    const pos = {};
    let rank = 8;
    for (const row of rows) {
      let file = 0;
      for (const ch of row) {
        if (/\d/.test(ch)) file += +ch;
        else {
          const side = ch === ch.toUpperCase() ? "w" : "b";
          const pc = side + ch.toUpperCase();
          pos["abcdefgh"[file] + rank] = pc;
          file++;
        }
      }
      rank--;
    }
    return { pos, sideToMove };
  }

  function fenFromPos(pos, turn) {
    const files = "abcdefgh";
    const ranks = [];

    for (let r = 8; r >= 1; r--) {
      let row = "";
      let empty = 0;

      for (let f = 0; f < 8; f++) {
        const sq = files[f] + r;
        const pc = pos[sq];

        if (!pc) {
          empty++;
          continue;
        }

        if (empty) {
          row += String(empty);
          empty = 0;
        }

        const side = pc[0];
        const piece = pc[1];
        row += side === "w" ? piece.toUpperCase() : piece.toLowerCase();
      }

      if (empty) row += String(empty);
      ranks.push(row);
    }

    return `${ranks.join("/")} ${turn} - - 0 1`;
  }

  function applyUCIMove(pos, uci) {
    const from = uci.slice(0, 2), to = uci.slice(2, 4), promo = uci[4];
    const piece = pos[from];
    if (!piece) return false;

    const castle =
      (piece === "wK" && from === "e1" && (to === "g1" || to === "c1")) ||
      (piece === "bK" && from === "e8" && (to === "g8" || to === "c8"));
    if (castle) {
      if (to === "g1") { delete pos.h1; pos.f1 = "wR"; }
      if (to === "c1") { delete pos.a1; pos.d1 = "wR"; }
      if (to === "g8") { delete pos.h8; pos.f8 = "bR"; }
      if (to === "c8") { delete pos.a8; pos.d8 = "bR"; }
    }

    delete pos[from];
    pos[to] = piece;
    if (promo) pos[to] = piece[0] + promo.toUpperCase();
    return true;
  }

 
  function shuffleInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function initWidget(root) {
    if (root.__mateInit) return;
    root.__mateInit = true;

    root.classList.add("timer-white");
    root.classList.remove("timer-black");

    const boardEl = root.querySelector("#board");
    const boardWrap = root.querySelector("#boardWrap");
    const hintBtn = root.querySelector("#hintBtn");
    const solveBtn = root.querySelector("#solveBtn");

   

    const shareBtn = root.querySelector("#shareBtn");
    const globalTopListEl = root.querySelector("#globalTopList");
    const globalTopMetaEl = root.querySelector("#globalTopMeta");
    
    const runHud = root.querySelector("#runHud");
    const runHudText = root.querySelector("#runHudText");
    const timerHud = root.querySelector("#timerHud");
    const timerHudText = root.querySelector("#timerHudText");

const statRunValue = null;
const statDailyValue = null;

const rewardBadges = null;
   
    const comboPop = root.querySelector("#comboPop");
    const comboPopText = root.querySelector("#comboPopText");
    const solveBadgeChip = root.querySelector("#solveBadgeChip");
    const solveGainText = root.querySelector("#solveGainText");

const endSolvedValue = root.querySelector("#endSolvedValue");
const endCoinsValue = root.querySelector("#endCoinsValue");
const endBadgeTierIcon = root.querySelector("#endBadgeTierIcon");
const endShopBtn = root.querySelector("#endShopBtn");
const endShareBtn = root.querySelector("#endShareBtn");



    /* ========= CHESS.JS LOADER ========= */
    let chessReadyPromise = null;

    function loadChessJs() {
      if (window.Chess) return Promise.resolve(true);
      if (chessReadyPromise) return chessReadyPromise;

      chessReadyPromise = new Promise((resolve) => {
        const existing = document.querySelector(`script[data-cr-chessjs="1"]`);
        if (existing) {
          existing.addEventListener("load", () => resolve(!!window.Chess), { once: true });
          existing.addEventListener("error", () => resolve(false), { once: true });
          setTimeout(() => resolve(!!window.Chess), 1200);
          return;
        }

        const script = document.createElement("script");
        script.src = CHESSJS_URL;
        script.async = true;
        script.defer = true;
        script.setAttribute("data-cr-chessjs", "1");
        script.onload = () => resolve(!!window.Chess);
        script.onerror = () => resolve(false);
        document.head.appendChild(script);
      });

      return chessReadyPromise;
    }

    loadChessJs();

    function uciToMoveObj(from, to, promoChar) {
      const m = { from, to };
      if (promoChar) m.promotion = String(promoChar).toLowerCase();
      return m;
    }

    function isMate(game) {
      if (!game) return false;
      if (typeof game.isCheckmate === "function") return !!game.isCheckmate();
      if (typeof game.in_checkmate === "function") return !!game.in_checkmate();

      let inCheck = false;
      if (typeof game.inCheck === "function") inCheck = !!game.inCheck();
      else if (typeof game.in_check === "function") inCheck = !!game.in_check();

      let moves = null;
      if (typeof game.moves === "function") {
        try { moves = game.moves(); } catch { moves = null; }
      }
      if (!Array.isArray(moves)) return false;
      return inCheck && moves.length === 0;
    }

    function cloneGame(game) {
      if (!game || !window.Chess) return null;
      try { return new window.Chess(game.fen()); } catch { return null; }
    }

    let chessGame = null;
    let puzzleFen = "";
    let preMoveUci = "";

    /* ========= AUDIO ========= */
    let audioCtx = null;
    const sfxBuf = Object.create(null);
    let audioUnlocked = false;
    let checkSfxLastMs = 0;
    let countdownSource = null;
    let countdownRunning = false;
    let sfxReadyPromise = null;
    let sfxPreloaded = false;

    function ensureAudio() {
      if (audioCtx) return audioCtx;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
      return audioCtx;
    }

    async function unlockAudio() {
      if (audioUnlocked) return true;
      const ctx = ensureAudio();
      if (!ctx) return false;
      try {
        if (ctx.state === "suspended") await ctx.resume();
        const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
        audioUnlocked = true;
        return true;
      } catch {
        return false;
      }
    }

    async function loadOne(name, url) {
      const ctx = ensureAudio();
      if (!ctx || !url) return;
      try {
        const arr = await fetch(url, { cache: "force-cache" }).then((r) => r.arrayBuffer());
        sfxBuf[name] = await ctx.decodeAudioData(arr);
      } catch {
        sfxBuf[name] = null;
      }
    }

    async function preloadSfx() {
      if (sfxPreloaded) return true;
      ensureAudio();
      await Promise.all([
        loadOne("countdown", SFX_URLS.countdown),
        loadOne("capture", SFX_URLS.capture),
        loadOne("check", SFX_URLS.check),
      ]);
      sfxPreloaded = true;
      return true;
    }

    function playSfx(name, atTime) {
      if (!audioUnlocked) return null;
      const ctx = ensureAudio();
      if (!ctx) return null;

      const buf = sfxBuf[name];
      if (!buf) return null;

      if (name === "countdown" && countdownSource) {
        try { countdownSource.stop(); } catch {}
        countdownSource = null;
      }

      const gain = ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(1, SFX_VOL[name] ?? 0.85));

      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(gain);
      gain.connect(ctx.destination);

      const when = (typeof atTime === "number" && isFinite(atTime)) ? atTime : ctx.currentTime;
      try { src.start(when); } catch {}

      if (name === "countdown") {
        countdownSource = src;
        src.onended = () => { countdownSource = null; };
      }
      return src;
    }

    function playCheckSfx() {
      const now = Date.now();
      if (now - checkSfxLastMs < CHECK_SFX_COOLDOWN_MS) return;
      checkSfxLastMs = now;
      playSfx("check");
    }

    function setSideToMove(side) {
      const isWhite = side === "w";
      root.classList.toggle("timer-white", isWhite);
      root.classList.toggle("timer-black", !isWhite);
    }

    function currentTurnSide() {
      ensureChessGameReady();
      if (chessGame && typeof chessGame.turn === "function") return chessGame.turn();
      return currentSideText.includes("WHITE") ? "w" : "b";
    }

const startOverlay = root.querySelector("#startOverlay");
const overlayName = root.querySelector("#overlayName");
const overlayStart = root.querySelector("#overlayStart");
const endOverlay = root.querySelector("#endOverlay");
const overlayPlayAgain = root.querySelector("#overlayPlayAgain");
const endSummaryText = root.querySelector("#endSummaryText");


    const solvePop = root.querySelector("#solvePop");
    const centerToast = root.querySelector("#centerToast");
    const toastBig = root.querySelector("#toastBig");
    const toastSub = root.querySelector("#toastSub");
    const puzzleIdEl = root.querySelector("#puzzle-id");

    let PUZZLES = [];
    let pos = {};
    let solution = [];
    let step = 0;
    let usedHint = false;
    let usedShowSolution = false;
    let puzzleDisqualified = false;

    let currentPuzzleIndex = 0;
    let currentSideText = "WHITE TO MOVE.";

    let heightRaf = 0;
    function syncColumnHeights() {
      if (heightRaf) cancelAnimationFrame(heightRaf);
      heightRaf = requestAnimationFrame(() => {
        if (!window.matchMedia || !window.matchMedia("(min-width: 900px)").matches) {
          root.style.setProperty("--board-h", "auto");
          return;
        }
        const rect = boardEl.getBoundingClientRect();
        const h = Math.round(rect.height || 0);
        if (h > 0) root.style.setProperty("--board-h", `${h}px`);
      });
    }
    window.addEventListener("resize", syncColumnHeights, { passive: true });

    const LS_HANDLE = "cr_handle_v2";
    function getHandle() {
      const h = (localStorage.getItem(LS_HANDLE) || "").trim();
      return h || "GUEST";
    }
    function setHandle(h) {
      const clean = String(h || "").trim().slice(0, 18);
      if (clean) localStorage.setItem(LS_HANDLE, clean);
      else localStorage.removeItem(LS_HANDLE);
    }

    const LS_DAILY_YMD = "cr_daily_ymd_v1";
    const LS_DAILY_SOLVED = "cr_daily_solved_v2";
    const LS_WEEK_KEY = "cr_week_key_v2";

    function getDailyState() {
      const now = getBerlinDate();
      const today = ymd(now);
      const wk = getISOWeekKey(now);

      const savedDay = localStorage.getItem(LS_DAILY_YMD) || "";
      let solved = parseInt(localStorage.getItem(LS_DAILY_SOLVED) || "0", 10);

      const savedWk = localStorage.getItem(LS_WEEK_KEY) || "";
      if (savedWk !== wk) localStorage.setItem(LS_WEEK_KEY, wk);
      if (savedDay !== today) {
        localStorage.setItem(LS_DAILY_YMD, today);
        solved = 0;
        localStorage.setItem(LS_DAILY_SOLVED, String(solved));
      }
      return { today, weekKey: wk, solved };
    }
    function incrementDailySolved() {
      const s = getDailyState();
      const solved = s.solved + 1;
      localStorage.setItem(LS_DAILY_SOLVED, String(solved));
    }

    
   function applyRewardToUI() {}

function hideReward() {}

function getBadgeMeta(count) {
  if (count === 3) return { key: "bronze", label: "🥉 BRONZE", rewardLabel: "🥉 Bronze", css: "badge-bronze" };
  if (count === 6) return { key: "silver", label: "🥈 SILVER", rewardLabel: "🥈 Silver", css: "badge-silver" };
  if (count === 9) return { key: "gold", label: "🥇 GOLD", rewardLabel: "🥇 Gold", css: "badge-gold" };
  return null;
}

function showRewardForBadge(_badgeKey) {}

function getBestUnlockedReward() {
  if (runState.badgesUnlocked.includes("gold")) return BADGE_REWARDS.gold;
  if (runState.badgesUnlocked.includes("silver")) return BADGE_REWARDS.silver;
  if (runState.badgesUnlocked.includes("bronze")) return BADGE_REWARDS.bronze;
  return null;
}

function getRewardStorageKey() {
  return "cr_reward_code";
}

function persistRewardCode(code) {
  try {
    if (code) sessionStorage.setItem(getRewardStorageKey(), String(code));
    else sessionStorage.removeItem(getRewardStorageKey());
  } catch {}
}

function getPersistedRewardCode() {
  try {
    return sessionStorage.getItem(getRewardStorageKey()) || "";
  } catch {
    return "";
  }
}


function applyRewardCodeAndGo(reward) {
  if (!reward || !reward.code) return;
  persistRewardCode(reward.code);

  const redirect = reward.redirect || "/collections/men";
  window.location.href =
    `/discount/${encodeURIComponent(reward.code)}?redirect=${encodeURIComponent(redirect)}`;
}

function installRewardCodeCheckoutGuard() {
  if (document.__crRewardCheckoutGuardInstalled) return;
  document.__crRewardCheckoutGuardInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const trigger = e.target.closest(
        'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-checkout], .cart__checkout-button, .checkout-button'
      );
      if (!trigger) return;

      const savedCode = getPersistedRewardCode();
      if (!savedCode) return;

      e.preventDefault();
      e.stopPropagation();

      window.location.href =
        `/discount/${encodeURIComponent(savedCode)}?redirect=${encodeURIComponent("/checkout")}`;
    },
    true
  );
}



function populateEndOverlay() {
  if (endSolvedValue) endSolvedValue.textContent = `${runState.solved || 0}`;
  if (endCoinsValue) endCoinsValue.textContent = `${runState.coins || 0}`;

  if (endBadgeTierIcon) {
    if (runState.badgesUnlocked.includes("gold")) {
      endBadgeTierIcon.textContent = "🥇";
    } else if (runState.badgesUnlocked.includes("silver")) {
      endBadgeTierIcon.textContent = "🥈";
    } else if (runState.badgesUnlocked.includes("bronze")) {
      endBadgeTierIcon.textContent = "🥉";
    } else {
      endBadgeTierIcon.textContent = "—";
    }
  }

  const bestReward = getBestUnlockedReward();

  if (endShopBtn) {
    endShopBtn.onclick = null;

    if (bestReward) {
      persistRewardCode(bestReward.code);

      endShopBtn.dataset.rewardCode = bestReward.code;
      endShopBtn.href = "#";
      endShopBtn.textContent = `SHOP NOW – ${String(bestReward.label || "REWARD").toUpperCase()}`;
      endShopBtn.hidden = false;
      endShopBtn.classList.add("blink");

      endShopBtn.onclick = (e) => {
        e.preventDefault();
        const code = endShopBtn.dataset.rewardCode || bestReward.code;
        applyRewardCodeAndGo({ ...bestReward, code });
      };
    } else {
      persistRewardCode("");
      delete endShopBtn.dataset.rewardCode;

      endShopBtn.hidden = true;
      endShopBtn.classList.remove("blink");
      endShopBtn.textContent = "SHOP NOW";
      endShopBtn.href = "#";
    }
  }
}


    function showComboPop(multiplier, coinGain) {
      if (!comboPop || !comboPopText || multiplier <= 1) return;
      const label = "SPEED BONUS";
      comboPopText.textContent = `${label} x${multiplier} • +${coinGain} COINS`;
      comboPop.hidden = false;
      comboPop.classList.remove("show");
      void comboPop.offsetWidth;
      comboPop.classList.add("show");
      setTimeout(() => {
        comboPop.classList.remove("show");
        comboPop.hidden = true;
      }, 920);
    }

    /* ========= GLOBAL LEADERBOARD (JSONP) ========= */
function renderGlobalTop(rows) {
  if (!globalTopListEl) return;
  globalTopListEl.innerHTML = "";

  if (!rows || !rows.length) {
    const li = document.createElement("li");
    li.className = "lb-item";
    li.innerHTML = `<span class="lb-left">NO ENTRIES YET</span><span class="lb-rightv">--</span>`;
    globalTopListEl.appendChild(li);
    return;
  }

  rows.forEach((r, i) => {
    const name = String(r.user_name || "GUEST").toUpperCase();
    const crown = i === 0 ? "👑 " : "";
    const coins = Number(r.coins || r.puzzles_solved_5min || 0);

    const li = document.createElement("li");
    li.className = "lb-item" + (i === 0 ? " top1" : "");
    li.innerHTML =
      `<span class="lb-left">${i + 1}. ${crown}${escapeHtml_(name)}</span>` +
      `<span class="lb-rightv">🪙 ${coins}</span>`;

    globalTopListEl.appendChild(li);
  });
}



function loadGlobalTop() {
    if (globalTopMetaEl) globalTopMetaEl.textContent = "UPDATED: LOADING…";

  const cbName = "cr_cb_" + Math.random().toString(36).slice(2, 9);
  const url = `${LEADERBOARD_API_URL}?action=top_all_time&limit=${encodeURIComponent(
    GLOBAL_TOP_LIMIT
  )}&callback=${encodeURIComponent(cbName)}`;

  window[cbName] = (data) => {
    try {
      if (data && data.ok) {
        renderGlobalTop(data.rows || []);
        const now = new Date();
        if (globalTopMetaEl) {
          globalTopMetaEl.textContent = `UPDATED: ${pad2(now.getHours())}:${pad2(
            now.getMinutes()
          )}:${pad2(now.getSeconds())}`;
        }
      } else {
        renderGlobalTop([]);
        if (globalTopMetaEl) globalTopMetaEl.textContent = "UPDATED: ERROR";
      }
    } finally {
      try { delete window[cbName]; } catch {}
    }
    syncColumnHeights();
  };

  const script = document.createElement("script");
  script.src = url;
  script.async = true;
  script.onerror = () => {
    renderGlobalTop([]);
    if (globalTopMetaEl) globalTopMetaEl.textContent = "UPDATED: ERROR";
    try { delete window[cbName]; } catch {}
    script.remove();
    syncColumnHeights();
  };
  script.onload = () => setTimeout(() => script.remove(), 0);
  document.body.appendChild(script);
}



    /* ========= RUN STATE ========= */
    const runState = {
      active: false,
      started: false,
      ended: false,
      sessionId: "",
      startMs: 0,
      timer: null,
      solved: 0,
      coins: 0,
      anyHint: false,
      anySolution: false,
      rewardUnlocked: false,
      badgesUnlocked: [],
      puzzleReadyAtMs: 0,
      puzzleOrder: [],
      puzzlePtr: 0,
      lockInput: false,
    };

    function setHudRun(n, bounce) {
      if (runHudText) runHudText.textContent = `🪙 ${runState.coins}`;
      if (statRunValue) statRunValue.textContent = String(n);

      if (bounce && runHud) {
        runHud.classList.remove("combo");
        void runHud.offsetWidth;
        runHud.classList.add("combo");
        setTimeout(() => runHud.classList.remove("combo"), 420);
      }
    }

    function setHudTimer(secLeft) {
      if (!timerHudText || !timerHud) return;

      timerHudText.textContent = fmtMMSS(secLeft);

      const panic = secLeft > 0 && secLeft <= PANIC_LAST_SECONDS;
      timerHud.classList.toggle("panic", panic);
      if (boardWrap) boardWrap.classList.toggle("panic-screen", panic);

      const dot = root.querySelector("#timerHudDot");
      if (!dot) return;

      dot.classList.remove("orange", "red", "pulse");

      if (secLeft <= 10) {
        dot.classList.add("red", "pulse");
      } else if (secLeft <= 20) {
        dot.classList.add("red");
      } else if (secLeft <= 60) {
        dot.classList.add("orange");
      }
    }

    function stopRunTimer() {
      if (runState.timer) clearInterval(runState.timer);
      runState.timer = null;
    }

    function submitRun(puzzlesSolved, coins, durationSec, hintUsed, solutionUsed) {
      const params = new URLSearchParams();
      params.set("action", "submit");
      params.set("token", LEADERBOARD_TOKEN);
      params.set("user_name", getHandle());
      params.set("session_id", runState.sessionId || "sess_" + Math.random().toString(36).slice(2, 10));
      params.set("puzzles_solved_3min", String(puzzlesSolved));
      params.set("puzzles_solved_5min", String(puzzlesSolved));
      params.set("coins", String(coins));
      params.set("duration_sec", String(durationSec));
      params.set("hint_used", hintUsed ? "1" : "0");
      params.set("solution_used", solutionUsed ? "1" : "0");
      params.set("source", "shopify");

      return fetch(LEADERBOARD_API_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: params.toString(),
      });
    }

    function showStartOverlay() {
      if (runState.started && !runState.ended) return;
      if (runState.lockInput || countdownRunning) return;

      if (!startOverlay) return;
      if (endOverlay && !endOverlay.hidden) return;
      startOverlay.hidden = false;
      if (overlayName) {
        overlayName.value = getHandle() === "GUEST" ? "" : getHandle();
        setTimeout(() => overlayName.focus({ preventScroll: true }), 30);
      }
      if (overlayStart) overlayStart.disabled = false;
    }
    function hideStartOverlay() { if (startOverlay) startOverlay.hidden = true; }
    function showEndOverlay() {
      hideStartOverlay();
      if (endOverlay) endOverlay.hidden = false;
    }
    function hideEndOverlay() { if (endOverlay) endOverlay.hidden = true; }

    function showCenterToast() { if (centerToast) centerToast.hidden = false; }
    function hideCenterToast() {
      if (centerToast) centerToast.hidden = true;
      root.classList.remove("toast-white", "toast-black");
    }
    function setToastBig(txt) { if (toastBig) toastBig.textContent = txt; }

    function setToastSub(txt, show) {
      if (!toastSub) return;

      toastSub.textContent = txt || "";
      toastSub.hidden = !show;

      root.classList.remove("toast-white", "toast-black");
      if (!show || !txt) return;

      const t = String(txt).toUpperCase();
      if (t.includes("WHITE")) root.classList.add("toast-white");
      else if (t.includes("BLACK")) root.classList.add("toast-black");
    }

    function triggerMiniFlash() {
      if (!boardWrap) return;
      boardWrap.classList.remove("mini-flash");
      void boardWrap.offsetWidth;
      boardWrap.classList.add("mini-flash");
      setTimeout(() => boardWrap.classList.remove("mini-flash"), 260);
    }

    async function countdownSequence() {
      if (countdownRunning) return;
      countdownRunning = true;

      runState.lockInput = true;
      showCenterToast();
      setToastSub("", false);

      if (sfxReadyPromise) {
        try { await sfxReadyPromise; } catch {}
      }

      const ctx = ensureAudio();
      const buf = sfxBuf.countdown;

      if (!ctx || !buf) {
        for (const n of ["3", "2", "1"]) {
          setToastBig(n);
          await new Promise((r) => setTimeout(r, 700));
        }
        setToastBig("");
        setToastSub(currentSideText.replace(".", ""), true);
        triggerMiniFlash();
        await new Promise((r) => setTimeout(r, 900));
        hideCenterToast();
        runState.lockInput = false;
        countdownRunning = false;
        return;
      }

      const startTime = ctx.currentTime + 0.06;
      playSfx("countdown", startTime);

      const waitUntil = async (targetTime) => {
        while (ctx.currentTime < targetTime) {
          await new Promise((r) => requestAnimationFrame(r));
        }
      };

      const cue = Math.max(0.2, buf.duration / 4);

      setToastBig("3");
      await waitUntil(startTime + cue * 1);

      setToastBig("2");
      await waitUntil(startTime + cue * 2);

      setToastBig("1");
      await waitUntil(startTime + cue * 3);

      setToastBig("");
      setToastSub(currentSideText.replace(".", ""), true);
      triggerMiniFlash();
      await waitUntil(startTime + cue * 4);

      hideCenterToast();
      runState.lockInput = false;
      countdownRunning = false;
    }

    async function flashSideToMoveEveryPuzzle(durationMs) {
      if (!runState.active) return;
      runState.lockInput = true;
      showCenterToast();
      setToastBig("");
      setToastSub(currentSideText.replace(".", ""), true);
      await new Promise((r) => setTimeout(r, durationMs));
      hideCenterToast();
      runState.lockInput = false;
      if (runState.active) runState.puzzleReadyAtMs = Date.now();
    }

    function ensurePuzzleOrder() {
      if (!PUZZLES.length) return;
      const idxs = Array.from({ length: PUZZLES.length }, (_, i) => i);
      shuffleInPlace(idxs);
      runState.puzzleOrder = idxs;
      runState.puzzlePtr = 0;
    }

    function nextPuzzleIndex() {
      if (!runState.puzzleOrder.length || runState.puzzlePtr >= runState.puzzleOrder.length) {
        ensurePuzzleOrder();
      }
      const idx = runState.puzzleOrder[runState.puzzlePtr++] ?? 0;
      return idx;
    }

    function loadPuzzleByIndex(idx) {
      if (!PUZZLES.length) return;
      currentPuzzleIndex = ((idx % PUZZLES.length) + PUZZLES.length) % PUZZLES.length;
      const p = PUZZLES[currentPuzzleIndex];
      loadPuzzle(p);
    }

    function posFromGame(game) {
      try { return parseFEN(game.fen()).pos; } catch { return null; }
    }

    function ensureChessGameReady() {
      if (chessGame || !window.Chess || !puzzleFen) return !!chessGame;

      try {
        chessGame = new window.Chess(puzzleFen);

        if (preMoveUci) {
          const f0 = preMoveUci.slice(0, 2), t0 = preMoveUci.slice(2, 4), p0 = preMoveUci[4];
          chessGame.move(uciToMoveObj(f0, t0, p0));
        }

        for (let i = 0; i < step; i++) {
          const uci = solution[i];
          if (!uci) break;
          const f = uci.slice(0, 2), t = uci.slice(2, 4), pr = uci[4];
          chessGame.move(uciToMoveObj(f, t, pr));
        }

        const newPos = posFromGame(chessGame);
        if (newPos) pos = newPos;
        boardEl.setPosition(pos);
        if (typeof chessGame.turn === "function") setSideToMove(chessGame.turn());
        return true;
      } catch {
        chessGame = null;
        return false;
      }
    }

function loadPuzzle(p) {
  const parsed = parseFEN(p.fen);
  const all = p.moves.trim().split(/\s+/).filter(Boolean);

  puzzleFen = p.fen;
  preMoveUci = "";
  chessGame = null;

  let baseGame = null;
  if (window.Chess) {
    try {
      baseGame = new window.Chess(p.fen);
    } catch {
      baseGame = null;
    }
  }

  // ROBUSTE AUTO-ERKENNUNG:
  // Wenn der erste Move aus der FEN legal spielbar ist,
  // behandeln wir ihn als Pre-Move.
  let shouldUsePreMove = false;
  if (baseGame && all.length > 0) {
    try {
      const testGame = new window.Chess(p.fen);
      const first = all[0];
      const moveObj = uciToMoveObj(first.slice(0, 2), first.slice(2, 4), first[4]);
      const testMove = testGame.move(moveObj);
      shouldUsePreMove = !!testMove;
    } catch {
      shouldUsePreMove = false;
    }
  }

  // Fallback ohne chess.js:
  // falls StartMode explizit direct sagt, respektieren wir das
  if (!baseGame) {
    shouldUsePreMove = (p.startMode || "").toLowerCase() !== "direct";
  }

  preMoveUci = shouldUsePreMove ? (all[0] || "") : "";

  if (window.Chess) {
    try {
      chessGame = new window.Chess(p.fen);
    } catch {
      chessGame = null;
    }
  }

  if (shouldUsePreMove && preMoveUci) {
    const from0 = preMoveUci.slice(0, 2);
    const to0 = preMoveUci.slice(2, 4);
    const promo0 = preMoveUci[4];

    if (chessGame) {
      try {
        const applied = chessGame.move(uciToMoveObj(from0, to0, promo0));
        if (!applied) {
          // falls doch nicht legal -> zurück auf direct
          chessGame = new window.Chess(p.fen);
          preMoveUci = "";
          pos = posFromGame(chessGame) || { ...parsed.pos };
          solution = all;
        } else {
          pos = posFromGame(chessGame) || { ...parsed.pos };
          solution = all.slice(1);
        }
      } catch {
        chessGame = null;
        pos = { ...parsed.pos };
        applyUCIMove(pos, preMoveUci);
        solution = all.slice(1);
      }
    } else {
      pos = { ...parsed.pos };
      applyUCIMove(pos, preMoveUci);
      solution = all.slice(1);
    }
  } else {
    pos = chessGame ? (posFromGame(chessGame) || { ...parsed.pos }) : { ...parsed.pos };
    solution = all;
  }

  const sideToMoveNow =
    chessGame && typeof chessGame.turn === "function"
      ? chessGame.turn()
      : (preMoveUci
          ? (parsed.sideToMove === "w" ? "b" : "w")
          : parsed.sideToMove);

  currentSideText = sideToMoveNow === "w" ? "WHITE TO MOVE." : "BLACK TO MOVE.";
  setSideToMove(sideToMoveNow);

  step = 0;
  usedHint = false;
  usedShowSolution = false;
  puzzleDisqualified = false;

  boardEl.clearHints();
  boardEl.highlight(null);
  boardEl.setPosition(pos);

  if (puzzleIdEl) {
    puzzleIdEl.textContent = `PUZZLE: ${String(p.id || "--").toUpperCase()}`;
  }

  syncColumnHeights();
}





    function showSolvePop(options = {}) {
      const badgeMeta = options.badgeMeta || null;
      const coinGain = Math.max(1, Number(options.coinGain || 1));

      if (solveGainText) {
        solveGainText.textContent = `🪙+${coinGain} REBEL COIN${coinGain === 1 ? "" : "S"}🪙`;
      }

      if (solveBadgeChip) {
        if (badgeMeta) {
          solveBadgeChip.hidden = false;
          solveBadgeChip.className = `solve-badge-chip arcade ${badgeMeta.css}`;
          solveBadgeChip.textContent = badgeMeta.label;
        } else {
          solveBadgeChip.hidden = true;
          solveBadgeChip.className = "solve-badge-chip arcade";
          solveBadgeChip.textContent = "";
        }
      }

      if (!solvePop) return;
      solvePop.classList.remove("show");
      void solvePop.offsetWidth;
      solvePop.classList.add("show");
      setTimeout(() => solvePop.classList.remove("show"), SOLVE_POP_CLEAR_MS);
    }

    function getComboMultiplier() {
      const now = Date.now();
      const readyAt = Number(runState.puzzleReadyAtMs || 0);
      if (!readyAt) return 1;
      const delta = now - readyAt;
      if (delta < COMBO_X3_MS) return 3;
      if (delta < COMBO_X2_MS) return 2;
      return 1;
    }

    async function startNewRun() {
      runState.active = false;
      runState.started = true;
      runState.ended = false;
      runState.sessionId = "sess_" + Math.random().toString(36).slice(2, 10);
      runState.startMs = 0;
      runState.solved = 0;
      runState.coins = 0;
      runState.anyHint = false;
      runState.anySolution = false;
      runState.rewardUnlocked = false;
      runState.badgesUnlocked = [];
      runState.puzzleReadyAtMs = 0;

      if (rewardBadges) rewardBadges.innerHTML = "";
      if (solveBadgeChip) solveBadgeChip.hidden = true;
      if (comboPop) {
        comboPop.classList.remove("show");
        comboPop.hidden = true;
      }

      hideEndOverlay();
      hideStartOverlay();
      hideReward();

      ensurePuzzleOrder();

      setHudRun(0, false);
      setHudTimer(RUN_SECONDS);
      if (boardWrap) boardWrap.classList.remove("panic-screen");
      if (statDailyValue) statDailyValue.textContent = String(getDailyState().solved);

      loadPuzzleByIndex(nextPuzzleIndex());
      await countdownSequence();

      runState.active = true;
      runState.startMs = Date.now();
      runState.puzzleReadyAtMs = Date.now();

      stopRunTimer();
      runState.timer = setInterval(() => {
        const elapsed = (Date.now() - runState.startMs) / 1000;
        const left = Math.max(0, RUN_SECONDS - elapsed);
        setHudTimer(left);
        if (left <= 0) endRun(true);
      }, 200);

      syncColumnHeights();
    }

    function endRun(_auto) {
      if (!runState.started) return;
      if (!runState.active && runState.ended) return;

      runState.active = false;
      runState.ended = true;
      runState.lockInput = true;
      stopRunTimer();
      if (boardWrap) boardWrap.classList.remove("panic-screen");

      hideStartOverlay();
      hideCenterToast();

      const elapsedSec = Math.min(
        RUN_SECONDS,
        Math.max(0, Math.floor((Date.now() - (runState.startMs || Date.now())) / 1000))
      );

      submitRun(runState.solved, runState.coins, elapsedSec, !!runState.anyHint, !!runState.anySolution)
        .finally(() => setTimeout(loadGlobalTop, 1200));
populateEndOverlay();
      showEndOverlay();
    }

    function finishSolved() {
      if (puzzleDisqualified) {
        if (runState.active) {
          setTimeout(async () => {
            if (!runState.active) return;
            const elapsed = (Date.now() - runState.startMs) / 1000;
            const left = RUN_SECONDS - elapsed;
            if (left <= 0) return;

            loadPuzzleByIndex(nextPuzzleIndex());
            await flashSideToMoveEveryPuzzle(POST_SOLVE_TOAST_MS);
          }, 250);
        }
        return;
      }

      incrementDailySolved();
      if (statDailyValue) statDailyValue.textContent = String(getDailyState().solved);

      if (runState.active) {
        const comboMultiplier = getComboMultiplier();
        const coinGain = comboMultiplier;
        runState.solved += 1;
        runState.coins += coinGain;

        setHudRun(runState.solved, true);
        if (usedHint) runState.anyHint = true;
        if (usedShowSolution) runState.anySolution = true;

        const badgeMeta = getBadgeMeta(runState.solved);

       if (badgeMeta && !runState.badgesUnlocked.includes(badgeMeta.key)) {
  runState.badgesUnlocked.push(badgeMeta.key);
  runState.rewardUnlocked = true;
  showRewardForBadge(badgeMeta.key);
}

        if (comboMultiplier > 1) {
          showComboPop(comboMultiplier, coinGain);
        }

        showSolvePop({ badgeMeta, coinGain });

        setTimeout(async () => {
          if (!runState.active) return;

          const elapsed = (Date.now() - runState.startMs) / 1000;
          const left = RUN_SECONDS - elapsed;
          if (left <= 0) return;

          loadPuzzleByIndex(nextPuzzleIndex());
          await flashSideToMoveEveryPuzzle(POST_SOLVE_TOAST_MS);
        }, SOLVE_POP_MS);

        return;
      }

      showSolvePop({ badgeMeta: null, coinGain: 1 });
    }

    function expectedMove() {
      const exp = solution[step];
      if (!exp) return null;
      return { from: exp.slice(0, 2), to: exp.slice(2, 4), uci: exp, promo: exp[4] };
    }

    function applyMoveFromGame(moveObj, fromSq, toSq) {
      const newPos = posFromGame(chessGame);
      if (newPos) pos = newPos;

      boardEl.setPosition(pos, { from: fromSq, to: toSq });

      const wasCapture = !!(moveObj && moveObj.captured);
      if (wasCapture && boardEl.explodeSquare) boardEl.explodeSquare(toSq);

      if (wasCapture) playSfx("capture");
      else {
        let inCheck = false;
        if (chessGame && typeof chessGame.inCheck === "function") inCheck = !!chessGame.inCheck();
        else if (chessGame && typeof chessGame.in_check === "function") inCheck = !!chessGame.in_check();
        if (inCheck) playCheckSfx();
      }
    }

    function applyMoveWithFx(fromSq, toSq, uciStr, promoMaybe) {
      if (window.Chess) {
        const turn = currentTurnSide();
        const fen = fenFromPos(pos, turn);

        let freshGame = null;
        try { freshGame = new window.Chess(fen); } catch { freshGame = null; }

        if (freshGame) {
          let m = null;
          try { m = freshGame.move(uciToMoveObj(fromSq, toSq, promoMaybe)); } catch { m = null; }
          if (!m) return false;

          chessGame = freshGame;
          applyMoveFromGame(m, fromSq, toSq);
          return true;
        }
      }

      const wasCapture = !!pos[toSq];
      applyUCIMove(pos, uciStr);
      boardEl.setPosition(pos, { from: fromSq, to: toSq });
      if (wasCapture && boardEl.explodeSquare) boardEl.explodeSquare(toSq);
      if (wasCapture) playSfx("capture");
      return true;
    }

    // ✅ FIXED: NON-promotion mate moves are accepted (boolean foundMate)
    function tryApplyMove(fromSq, toSq) {
      const exp = expectedMove();
      if (!exp) return { ok: false, reason: "done" };

      if (window.Chess) {
        const turn = currentTurnSide();
        const fen = fenFromPos(pos, turn);

        const promoCandidates = [null, "q", "r", "b", "n"];
        let foundMate = false;
        let foundPromo = null;

        for (const pr of promoCandidates) {
          let gg = null;
          try { gg = new window.Chess(fen); } catch { gg = null; }
          if (!gg) break;

          const m = gg.move(uciToMoveObj(fromSq, toSq, pr));
          if (!m) continue;

          if (isMate(gg)) {
            foundMate = true;
            foundPromo = pr;
            break;
          }
        }

        if (foundMate) {
          const uci = `${fromSq}${toSq}${foundPromo ? foundPromo : ""}`;
          if (!applyMoveWithFx(fromSq, toSq, uci, foundPromo)) {
            return { ok: false, reason: "wrong" };
          }

          step = solution.length;
          boardEl.clearHints();
          boardEl.highlight(null);
          setTimeout(() => finishSolved(), 250);
          return { ok: true };
        }
      }

      if (fromSq === exp.from && toSq === exp.to) {
        if (!applyMoveWithFx(exp.from, exp.to, exp.uci, exp.promo)) {
          return { ok: false, reason: "wrong" };
        }
        step++;
        boardEl.clearHints();
        boardEl.highlight(null);
        setTimeout(autoReply, 650);
        return { ok: true };
      }

      return { ok: false, reason: "wrong" };
    }

    function canInteract() {
      return runState.started && runState.active && !runState.ended && !runState.lockInput;
    }

    boardEl.addEventListener("squareclick", (e) => {
      if (runState.ended) { showEndOverlay(); return; }
      if (!runState.started) { showStartOverlay(); return; }
      if (!runState.active || runState.lockInput || countdownRunning) return;
      if (!canInteract()) return;

      const sq = e.detail.square;
      const exp = expectedMove();
      if (!exp) return;

      const piece = pos[sq] || null;
      const turn = currentTurnSide();
      const isOwnPiece = !!piece && piece[0] === turn;

      // Noch nichts selektiert -> nur eigene Figur anwählbar
      if (!boardEl.selected) {
        if (!isOwnPiece) return;
        boardEl.highlight(sq);
        return;
      }

      const sel = boardEl.selected;

      // Klick auf dieselbe Figur -> deselect
      if (sel === sq) {
        boardEl.highlight(null);
        return;
      }

      // Klick auf andere eigene Figur -> Auswahl wechseln
      if (isOwnPiece) {
        boardEl.highlight(sq);
        return;
      }

      // Sonst: Zugversuch auf Ziel-Feld
      const res = tryApplyMove(sel, sq);
      if (!res.ok) boardEl.highlight(null);
    });


    boardEl.addEventListener("pieceMove", (e) => {
      if (runState.ended) { showEndOverlay(); return; }
      if (!runState.started) { showStartOverlay(); return; }
      if (!runState.active || runState.lockInput || countdownRunning) return;
      if (!canInteract()) return;

      const { from, to } = e.detail || {};
      if (!from || !to) return;
      boardEl.highlight(null);
      tryApplyMove(from, to);
    });

    function autoReply() {
      boardEl.highlight(null);

      if (step < solution.length && step % 2 === 1) {
        const uci = solution[step];
        const from = uci.slice(0, 2);
        const to = uci.slice(2, 4);
        const promo = uci[4];

        applyMoveWithFx(from, to, uci, promo);
        step++;
      }

      if (step >= solution.length) finishSolved();
      syncColumnHeights();
    }

    if (hintBtn) hintBtn.onclick = () => {
      if (!runState.started) { showStartOverlay(); return; }
      if (!runState.active || runState.lockInput || countdownRunning) return;
      if (!canInteract()) return;
      const exp = expectedMove();
      if (!exp) return;
      usedHint = true;
      boardEl.setHints(exp.from, exp.to);
    };

    if (solveBtn) solveBtn.onclick = async () => {
      if (!runState.started) { showStartOverlay(); return; }
      if (!runState.active || runState.lockInput || countdownRunning) return;
      if (!canInteract()) return;

      usedShowSolution = true;
      puzzleDisqualified = true;
      boardEl.clearHints();
      runState.lockInput = true;

      while (step < solution.length) {
        const uci = solution[step];
        const from = uci.slice(0, 2);
        const to = uci.slice(2, 4);
        const promo = uci[4];

        boardEl.highlight(null);
        applyMoveWithFx(from, to, uci, promo);

        step++;
        await new Promise((r) => setTimeout(r, 650));
      }

      runState.lockInput = false;
      finishSolved();
    };

    function doStartFromOverlay() {
      if (runState.started && !runState.ended) return;
      if (overlayStart) overlayStart.disabled = true;

      const raw = (overlayName && overlayName.value ? overlayName.value : "").trim();
      if (raw) setHandle(raw);

      sfxReadyPromise = (async () => {
        const ok = await unlockAudio();
        if (!ok) return false;
        await preloadSfx();
        return true;
      })();

      loadChessJs();

      hideStartOverlay();
      hideEndOverlay();
      startNewRun().finally(() => {
        if (overlayStart) overlayStart.disabled = false;
      });
    }

    if (overlayStart) overlayStart.onclick = doStartFromOverlay;
    if (overlayName) {
      overlayName.addEventListener("keydown", (e) => {
        if (e.key === "Enter") { e.preventDefault(); doStartFromOverlay(); }
      });
    }

    if (overlayPlayAgain) overlayPlayAgain.onclick = () => {
      runState.active = false;
      runState.started = false;
      runState.ended = false;
      runState.lockInput = false;
      countdownRunning = false;
      runState.coins = 0;
      runState.puzzleReadyAtMs = 0;
      runState.badgesUnlocked = [];
persistRewardCode("");



if (endShopBtn) {
  endShopBtn.hidden = true;
  endShopBtn.classList.remove("blink");
  endShopBtn.textContent = "SHOP NOW";
  endShopBtn.href = "#";
}
if (endSolvedValue) endSolvedValue.textContent = "0";
if (endCoinsValue) endCoinsValue.textContent = "0";



      stopRunTimer();
      hideEndOverlay();
      hideCenterToast();
      hideReward();

      if (rewardBadges) rewardBadges.innerHTML = "";
      if (solveBadgeChip) solveBadgeChip.hidden = true;
      if (comboPop) {
        comboPop.classList.remove("show");
        comboPop.hidden = true;
      }

      setHudRun(0, false);
      setHudTimer(RUN_SECONDS);
      setSideToMove("w");

      if (statDailyValue) statDailyValue.textContent = String(getDailyState().solved);
      if (boardWrap) boardWrap.classList.remove("panic-screen");

      ensurePuzzleOrder();
      loadPuzzleByIndex(nextPuzzleIndex());

      showStartOverlay();
    };

    function buildShareText() {
      const daily = getDailyState();
      const handle = getHandle();
      return [
        `I SOLVED ${daily.solved} PUZZLE${daily.solved === 1 ? "" : "S"} TODAY ON CHESSREBELS ♟️🔥`,
        ``,
        `PLAYER: ${handle}`,
        `TRY IT: ${SHARE_SITE_URL}`,
        `FOLLOW @chessrebels_official`,
      ].join("\n");
    }
    async function copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          const ok = document.execCommand("copy");
          ta.remove();
          return !!ok;
        } catch {
          return false;
        }
      }
    }
function openShareMenu(text, url) {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);

  const oldMenu = document.getElementById("cr-share-menu");
  const oldBackdrop = document.getElementById("cr-share-backdrop");
  if (oldMenu) oldMenu.remove();
  if (oldBackdrop) oldBackdrop.remove();

  const backdrop = document.createElement("div");
  backdrop.id = "cr-share-backdrop";
  backdrop.style.position = "fixed";
  backdrop.style.inset = "0";
  backdrop.style.background = "rgba(0,0,0,.45)";
  backdrop.style.zIndex = "999998";

  const menu = document.createElement("div");
  menu.id = "cr-share-menu";
  menu.style.position = "fixed";
  menu.style.left = "50%";
  menu.style.top = "50%";
  menu.style.transform = "translate(-50%, -50%)";
  menu.style.zIndex = "999999";
  menu.style.width = "min(92vw, 340px)";
  menu.style.background = "linear-gradient(180deg, #63d8ff 0%, #46a9ff 100%)";
  menu.style.color = "#fff";
  menu.style.border = "4px solid #000";
  menu.style.borderRadius = "24px";
  menu.style.padding = "18px 16px 16px";
  menu.style.display = "flex";
  menu.style.flexDirection = "column";
  menu.style.gap = "12px";
  menu.style.boxShadow = "0 12px 0 rgba(0,0,0,.18), 0 24px 42px rgba(0,0,0,.22)";
  menu.style.fontFamily = 'var(--cr-font, "Archivo Black", system-ui, sans-serif)';

  menu.innerHTML = `
    <div style="
      align-self:center;
      min-height:38px;
      padding:8px 16px;
      border:3px solid #000;
      border-radius:999px;
      background:linear-gradient(180deg, #fff2a8 0%, #ffd84d 100%);
      color:#111;
      box-shadow:0 6px 0 rgba(0,0,0,.18);
      font-size:14px;
      letter-spacing:.4px;
      text-transform:uppercase;
      font-weight:900;
    ">Share Run</div>

    <div style="
      text-align:center;
      font-size:18px;
      line-height:1;
      text-transform:uppercase;
      font-weight:900;
      text-shadow:
        2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000,
        2px 2px #000, -2px 2px #000, 2px -2px #000, -2px -2px #000;
    ">Choose Where</div>

  <button id="shareCopy" type="button" style="${shareBtnStyle("linear-gradient(180deg, #8fd3ff 0%, #3d8bff 100%)")}">
    Copy Text
  </button>

  <button id="shareX" type="button" style="${shareBtnStyle("linear-gradient(180deg, #111 0%, #000 100%)")}">
    Share on X
  </button>

  <button id="shareWA" type="button" style="${shareBtnStyle("linear-gradient(180deg, #9cff7f 0%, #35d96f 100%)")}">
    Share on WhatsApp
  </button>

  <button id="shareClose" type="button" style="${shareBtnStyle("linear-gradient(180deg, #555 0%, #2b2b2b 100%)")}">
    Close
  </button>
`;


  function shareBtnStyle(bg) {
    return [
      "width:100%",
      "min-height:54px",
      "padding:0 18px",
      "border:4px solid #000",
      "border-radius:16px",
      `background:${bg}`,
      "color:#fff",
      'font:900 17px/1 var(--cr-font, "Archivo Black", system-ui, sans-serif)',
      "letter-spacing:.6px",
      "text-transform:uppercase",
      "cursor:pointer",
      "box-shadow:0 8px 0 rgba(0,0,0,.18), 0 14px 24px rgba(0,0,0,.12)"
    ].join(";");
  }

  function closeMenu() {
    menu.remove();
    backdrop.remove();
    document.removeEventListener("keydown", onKeyDown);
  }

  function onKeyDown(e) {
    if (e.key === "Escape") closeMenu();
  }

  function pressEffect(btn) {
    if (!btn) return;
    btn.addEventListener("mousedown", () => {
      btn.style.transform = "translateY(3px)";
      btn.style.boxShadow = "0 5px 0 rgba(0,0,0,.18), 0 10px 16px rgba(0,0,0,.12)";
    });
    const reset = () => {
      btn.style.transform = "translateY(0)";
      btn.style.boxShadow = "0 8px 0 rgba(0,0,0,.18), 0 14px 24px rgba(0,0,0,.12)";
    };
    btn.addEventListener("mouseup", reset);
    btn.addEventListener("mouseleave", reset);
  }

  document.addEventListener("keydown", onKeyDown);
  backdrop.addEventListener("click", closeMenu);

  const copyBtn = menu.querySelector("#shareCopy");
  const xBtn = menu.querySelector("#shareX");
  const waBtn = menu.querySelector("#shareWA");
  const closeBtn = menu.querySelector("#shareClose");

  pressEffect(copyBtn);
  pressEffect(xBtn);
  pressEffect(waBtn);
  pressEffect(closeBtn);

  copyBtn.onclick = async () => {
    const ok = await copyToClipboard(text);
    copyBtn.textContent = ok ? "Copied ✓" : "Copy Failed";
    setTimeout(closeMenu, 700);
  };

  xBtn.onclick = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodedText}%20${encodedUrl}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  waBtn.onclick = () => {
    window.open(
      `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  closeBtn.onclick = closeMenu;

  document.body.appendChild(backdrop);
  document.body.appendChild(menu);
}


if (shareBtn) {
  shareBtn.onclick = async () => {
    if (!sfxReadyPromise) {
      sfxReadyPromise = (async () => {
        const ok = await unlockAudio();
        if (!ok) return false;
        await preloadSfx();
        return true;
      })();
    }




    const shareText = buildShareText();
    const shareUrl = SHARE_SITE_URL;

    // Mobile / Browser mit nativer Share-Unterstützung:
    // Bei Erfolg ODER Abbruch KEIN eigenes Popup öffnen.
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Chessrebels Puzzle",
          text: shareText,
          url: shareUrl
        });
      } catch (_err) {
        // User hat Share-Menü geschlossen / abgebrochen:
        // bewusst kein Fallback
      }
      return;
    }

    // Nur Desktop-/Fallback-Browser ohne navigator.share
    openShareMenu(shareText, shareUrl);
  };
}

if (endShareBtn) {
  endShareBtn.onclick = async () => {
    const shareText = buildShareText();
    const shareUrl = SHARE_SITE_URL;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Chessrebels Puzzle",
          text: shareText,
          url: shareUrl
        });
      } catch (_err) {
        // kein Fallback bei Abbruch
      }
      return;
    }

    openShareMenu(shareText, shareUrl);
  };
}



    (async () => {
      runState.started = false;
      runState.active = false;
      runState.ended = false;
      runState.lockInput = false;
      runState.coins = 0;
      runState.puzzleReadyAtMs = 0;
      countdownRunning = false;
      stopRunTimer();

      hideReward();
      hideEndOverlay();
      hideCenterToast();
      showStartOverlay();
persistRewardCode("");
installRewardCodeCheckoutGuard();

      setHudRun(0, false);
      setHudTimer(RUN_SECONDS);
      setSideToMove("w");
      if (statDailyValue) statDailyValue.textContent = String(getDailyState().solved);
      if (boardWrap) boardWrap.classList.remove("panic-screen");

      loadGlobalTop();

      PUZZLES = await loadPuzzles();
      if (customElements.whenDefined) await customElements.whenDefined("mini-chessboard");

      ensurePuzzleOrder();
      loadPuzzleByIndex(nextPuzzleIndex());
      setTimeout(syncColumnHeights, 50);
      setTimeout(syncColumnHeights, 250);
    })();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();



